// API Configuration
import config from './config';
import cache from './cache';

// Timeout wrapper for fetch requests
const fetchWithTimeout = async (url, options = {}, timeout = 30000) => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal
    });
    clearTimeout(timeoutId);
    return response;
  } catch (error) {
    clearTimeout(timeoutId);
    if (error.name === 'AbortError') {
      throw new Error('Request timeout - the server took too long to respond. Please try again.');
    }
    throw error;
  }
};

// Goal tracking error mapping
const GOAL_TRACKING_ERRORS = {
  'ENTRY_ALREADY_EXISTS': 'A goal tracking entry already exists for this date.',
  'INVALID_DATE_FORMAT': 'Date must be a Sunday (week ending).',
  'ENTRY_TOO_OLD': 'Entry is older than 7 days and cannot be edited.',
  'FEATURE_NOT_ENABLED': 'This compensation feature is not enabled for your account.',
  'INSUFFICIENT_PERMISSIONS': 'You do not have permission to access this data.',
  'BUSINESS_MISMATCH': 'Access denied: User belongs to different business.',
  'INVALID_METRICS': 'Invalid data: Please check your entries and try again.',
  'USER_NOT_FOUND': 'User account not found.',
  'ALREADY_CLOCKED_IN': 'You are already clocked in.',
  'NOT_CLOCKED_IN': 'You are not currently clocked in.'
};

// Helper function to map backend errors to user-friendly messages
const mapGoalTrackingError = (backendError) => {
  return GOAL_TRACKING_ERRORS[backendError] || backendError || 'An error occurred';
};

// Helper function to extract businessId from token for cache keys
// SECURITY: Never stores token, only extracts businessId
const getBusinessIdFromToken = (token) => {
  try {
    const payload = token.split('.')[1];
    const decoded = JSON.parse(atob(payload));
    return decoded.businessId || decoded.tennantId || decoded.tenantId || null;
  } catch (error) {
    // Silently handle error
    return null;
  }
};

// Helper function to build URL with optional function key
const buildUrl = (service, endpoint) => {
  // In development, use localhost proxy for all services to avoid CORS
  if (import.meta.env.DEV) {
    const url = `/api${endpoint}`;
    
    if (config.USE_FUNCTION_KEYS) {
      // Use master host key if available (works for all functions)
      if (config.MASTER_HOST_KEY) {
        return `${url}?code=${config.MASTER_HOST_KEY}`;
      }
      
      // Use individual function key
      const functionName = endpoint.replace('/', '');
      const functionKey = config.FUNCTION_KEYS[functionName];
      if (functionKey) {
        return `${url}?code=${functionKey}`;
      }
    }
    
    return url;
  }
  
  // For production, use direct URLs
  const baseUrl = config.API_URLS[service];
  
  if (!baseUrl) {
    throw new Error(`Unknown service: ${service}`);
  }
  
  const url = `${baseUrl}${endpoint}`;
  
  if (config.USE_FUNCTION_KEYS) {
    // Use master host key if available (works for all functions)
    if (config.MASTER_HOST_KEY) {
      return `${url}?code=${config.MASTER_HOST_KEY}`;
    }
    
    // Use individual function key
    const functionName = endpoint.replace('/', '');
    const functionKey = config.FUNCTION_KEYS[functionName];
    if (functionKey) {
      return `${url}?code=${functionKey}`;
    }
  }
  
  return url;
};

// API Service Functions
export const apiService = {
  // Login function
  async login(email, password) {
    try {
      const response = await fetch(buildUrl('logreg', '/login'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          password
        })
      });

      const data = await response.json();

      // Check the success field in the response body, not HTTP status
      if (!data.success) {
        return {
          success: false,
          error: data.error || 'Login failed',
          isEduUser: !!data.isEduUser
        };
      }

      // Return ALL fields from backend, not just token and refreshKey
      return {
        success: true,
        data: data
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  // Business registration function
  async registerBusiness(formData) {
    try {
      const response = await fetch(buildUrl('logreg', '/register'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (!data.success) {
        return {
          success: false,
          error: data.error || 'Registration failed'
        };
      }

      return {
        success: true,
        data: data.data
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  // Refresh session token
  async refreshSessionToken(refreshKey) {
    try {
      const response = await fetch(buildUrl('logreg', '/refreshsessionkey'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          refreshKey
        })
      });

      const data = await response.json();

      if (!data.success) {
        return {
          success: false,
          error: data.error || 'Failed to refresh session'
        };
      }

      return {
        success: true,
        data: {
          sessionToken: data.sessionToken,  // Backend returns 'sessionToken', not 'token'
          token: data.sessionToken,         // Also provide as 'token' for backward compatibility
          refreshKey: data.refreshKey
        }
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  // Send employee invites
  async sendEmployeeInvites(token, invites) {
    try {
      const response = await fetch(buildUrl('logreg', '/sendemployeeinvites'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          sessionToken: token,
          invites: invites
        })
      });

      const data = await response.json();
      if (!data.success) {
        return { success: false, error: data.error || 'Failed to send employee invites' };
      }

      // Check per-invite results — backend returns { success: true, results: [{ email, succeeded, reason }] }
      if (data.results && Array.isArray(data.results)) {
        const reasonLabels = {
          already_an_employee: 'Already an employee in your business',
          user_in_another_business: 'This user belongs to another business',
          invalid_email_format: 'Invalid email format',
          EMAIL_SEND_FAILED: 'Failed to send email',
          failed_to_link_user: 'Failed to add user to business',
          failed_to_create_invite: 'Failed to create invite'
        };
        const failed = data.results.filter(r => !r.succeeded);
        if (failed.length > 0) {
          const reasons = failed.map(f => `${f.email}: ${reasonLabels[f.reason] || f.reason}`).join('; ');
          return { success: false, error: reasons, results: data.results };
        }
      }

      return data;
    } catch (error) {
      return { success: false, error: error.message || 'Network error occurred' };
    }
  },

  // Get pending employee invites
  async getPendingInvites(token) {
    const response = await fetch(buildUrl('logreg', '/getpendinginvites'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        sessionToken: token
      })
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  },

  // Send password reset email
  async sendPasswordResetEmail(email) {
    try {
      const response = await fetch(buildUrl('logreg', '/passwordresetemail'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return { success: true, data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  // Send discovery call request email
  async sendDiscoveryCallEmail(formData) {
    try {
      const response = await fetch(buildUrl('logreg', '/discoverycall'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return { success: true, data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  // Reset password with token
  async resetPassword(resetToken, newPassword) {
    try {
      const response = await fetch(buildUrl('logreg', '/resetpassword'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ resetToken, newPassword })
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        return { success: false, error: data.error || 'Failed to reset password' };
      }

      return { success: true, data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  // Set password for business invite tokens (calls hrma-be-admin/setpassword)
  async setInvitePassword(token, password) {
    try {
      const response = await fetch(buildUrl('admin', '/setpassword'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ token, password })
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        return { success: false, error: data.error || 'Failed to set password' };
      }

      return { success: true, data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  // Revoke (cancel) an employee invite
  async revokeEmployeeInvite(token, inviteToken) {
    try {
      const response = await fetch(buildUrl('logreg', '/revokeemployeeinvite'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          sessionToken: token,
          inviteToken: inviteToken
        })
      });

      const data = await response.json();
      if (!data.success) {
        return { success: false, error: data.error || `Failed to revoke invite (HTTP ${response.status})` };
      }
      return data;
    } catch (error) {
      return { success: false, error: error.message || 'Network error occurred' };
    }
  },

  // Clock in
  async clockIn(token) {
    try {
      const response = await fetch(buildUrl('tracktime', '/clockin'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          sessionToken: token
        })
      });

      if (!response.ok) {
        return {
          success: false,
          error: `HTTP ${response.status}: ${response.statusText}`
        };
      }

      const data = await response.json();
      return {
        success: data.success,
        data: data.success ? {
          message: data.message,
          clockInTime: data.clockInTime,
          clockId: data.clockId
        } : null,
        error: data.success ? null : (data.error || 'Failed to clock in')
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  // Clock out
  async clockOut(token, isTraining = false) {
    try {
      const response = await fetch(buildUrl('tracktime', '/clockout'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          sessionToken: token,
          isTraining: Boolean(isTraining)
        })
      });

      if (!response.ok) {
        return {
          success: false,
          error: `HTTP ${response.status}: ${response.statusText}`
        };
      }

      const data = await response.json();
      return {
        success: data.success,
        data: data.success ? {
          message: data.message,
          clockOutTime: data.clockOutTime
        } : null,
        error: data.success ? null : (data.error || 'Failed to clock out')
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  // Get clock status (single employee)
  async getClockStatus(token) {
    try {
      const response = await fetch(buildUrl('tracktime', '/getclockstatussingle'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          sessionToken: token
        })
      });

      if (!response.ok) {
        return {
          success: false,
          error: `HTTP ${response.status}: ${response.statusText}`
        };
      }

      const data = await response.json();
      return {
        success: data.success,
        data: data.success ? {
          isActive: data.isActive,
          lastActivity: data.lastActivity,
          clockInTime: data.clockInTime,
          clockOutTime: data.clockOutTime,
          totalHoursToday: data.totalHoursToday,
          isTraining: data.isTraining === true
        } : null,
        error: data.success ? null : (data.error || 'Failed to get clock status')
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  // Get clock status for all employees (business owner/manager only)
  async getClockStatusMultiple(token) {
    try {
      const response = await fetch(buildUrl('tracktime', '/getclockstatusmultiple'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          sessionToken: token
        })
      });

      if (!response.ok) {
        return {
          success: false,
          error: `HTTP ${response.status}: ${response.statusText}`
        };
      }

      const data = await response.json();
      return {
        success: data.success,
        data: data.success ? data.data : null,
        summary: data.success ? data.summary : null,
        error: data.success ? null : (data.error || 'Failed to get employee clock statuses')
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  // Get clock history for an employee
  async getClockHistory(token, userId = null, fromDate = null, toDate = null, pageNumber = 1, pageSize = 50) {
    try {
      const response = await fetch(buildUrl('tracktime', '/getclockhistory'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          sessionToken: token,
          userId,
          fromDate,
          toDate,
          pageNumber,
          pageSize
        })
      });

      if (!response.ok) {
        return {
          success: false,
          error: `HTTP ${response.status}: ${response.statusText}`
        };
      }

      const data = await response.json();
      return {
        success: data.success,
        data: data.success ? data.data : null,
        summary: data.success ? data.summary : null,
        error: data.success ? null : (data.error || 'Failed to get clock history')
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  // Get time reports for an employee
  async getTimeReports(token, options = {}) {
    try {
      const { userId, employeeId, startDate, endDate, reportType = 'weekly_summary' } = options;
      const response = await fetch(buildUrl('tracktime', '/gettimereports'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          sessionToken: token,
          userId: userId || employeeId,
          startDate,
          endDate,
          reportType
        })
      });

      if (!response.ok) {
        return {
          success: false,
          error: `HTTP ${response.status}: ${response.statusText}`
        };
      }

      const data = await response.json();
      return {
        success: data.success,
        data: data.success ? data.data : null,
        error: data.success ? null : (data.error || 'Failed to get time reports')
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  // Get hours summary (service + training hours) for a date range
  async getHoursSummary(token, userId = null, fromDate = null, toDate = null) {
    try {
      const response = await fetch(buildUrl('tracktime', '/gethourssummary'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          sessionToken: token,
          userId,
          fromDate,
          toDate
        })
      });

      if (!response.ok) {
        return {
          success: false,
          error: `HTTP ${response.status}: ${response.statusText}`
        };
      }

      const data = await response.json();
      return {
        success: data.success,
        data: data.success ? {
          hoursWorked: data.hoursWorked,
          hoursTraining: data.hoursTraining,
          totalHours: data.totalHours,
          period: data.period
        } : null,
        error: data.success ? null : (data.error || 'Failed to get hours summary')
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  // Override/edit clock entry (business owner/manager only)
  async overrideClock(token, clockId, clockInTime, clockOutTime, isTraining) {
    try {
      const response = await fetch(buildUrl('tracktime', '/overrideclock'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          sessionToken: token,
          clockId,
          clockInTime,
          clockOutTime,
          isTraining: Boolean(isTraining)
        })
      });

      if (!response.ok) {
        return {
          success: false,
          error: `HTTP ${response.status}: ${response.statusText}`
        };
      }

      const data = await response.json();
      return {
        success: data.success,
        data: data.success ? data.data : null,
        error: data.success ? null : (data.error || 'Failed to override clock entry')
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  // Get business profile information
  async getBusinessProfile(token) {
    try {
      const response = await fetch(buildUrl('managebusiness', '/getbusiness'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          sessionToken: token
        })
      });

      // Check if response is ok
      if (!response.ok) {
        return {
          success: false,
          error: `HTTP ${response.status}: ${response.statusText}`
        };
      }

      // Check if response has content
      const responseText = await response.text();
      if (!responseText) {
        return {
          success: false,
          error: 'Empty response from server'
        };
      }

      let data;
      try {
        data = JSON.parse(responseText);
      } catch (parseError) {
        return {
          success: false,
          error: 'Invalid JSON response from server'
        };
      }

      if (!data.success) {
        return {
          success: false,
          error: data.error || 'Failed to fetch business profile'
        };
      }

      const result = data.business || data.data;

      return {
        success: true,
        data: result
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  // Edit business profile information
  async editBusinessProfile(token, businessData) {
    try {
      const response = await fetch(buildUrl('managebusiness', '/editbusiness'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          sessionToken: token,
          businessName: businessData.businessName,
          legalEntityName: businessData.legalEntityName,
          businessRegistrationNumber: businessData.businessRegistrationNumber,
          taxNumber: businessData.taxNumber,
          country: businessData.country
          // Note: email is intentionally excluded to make it read-only in frontend
        })
      });

      // Check if response is ok
      if (!response.ok) {
        return {
          success: false,
          error: `HTTP ${response.status}: ${response.statusText}`
        };
      }

      // Check if response has content
      const responseText = await response.text();
      if (!responseText) {
        return {
          success: false,
          error: 'Empty response from server'
        };
      }

      let data;
      try {
        data = JSON.parse(responseText);
      } catch (parseError) {
        return {
          success: false,
          error: 'Invalid JSON response from server'
        };
      }

      if (!data.success) {
        return {
          success: false,
          error: data.error || 'Failed to update business profile'
        };
      }

      // CACHE INVALIDATION: Clear business profile cache on successful edit
      const businessId = getBusinessIdFromToken(token);
      if (businessId) {
        cache.invalidate('businessProfile', businessId);
      }

      return {
        success: true,
        data: data.data
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  // Get employee profile information (self)
  async getEmployeeProfile(token, employeeId) {
    try {
      const response = await fetch(buildUrl('manageemployees', '/getemployee'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          sessionToken: token,
          employeeId: employeeId
        })
      });

      // Check if response is ok
      if (!response.ok) {
        return {
          success: false,
          error: `HTTP ${response.status}: ${response.statusText}`
        };
      }

      // Check if response has content
      const responseText = await response.text();
      if (!responseText) {
        return {
          success: false,
          error: 'Empty response from server'
        };
      }

      let data;
      try {
        data = JSON.parse(responseText);
      } catch (parseError) {
        return {
          success: false,
          error: 'Invalid JSON response from server'
        };
      }

      if (!data.success) {
        return {
          success: false,
          error: data.error || 'Failed to fetch employee profile'
        };
      }

      return {
        success: true,
        data: data.data
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  // Edit employee profile (self) - employees can only edit limited fields
  async editEmployeeProfileSelf(token, profileData) {
    try {
      const response = await fetch(buildUrl('manageemployees', '/editemployeeprofileself'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          sessionToken: token,
          mobileNumber: profileData.mobileNumber,
          preferredName: profileData.preferredName,
          gender: profileData.gender,
          birthday: profileData.birthday
        })
      });

      // Check if response is ok
      if (!response.ok) {
        return {
          success: false,
          error: `HTTP ${response.status}: ${response.statusText}`
        };
      }

      // Check if response has content
      const responseText = await response.text();
      if (!responseText) {
        return {
          success: false,
          error: 'Empty response from server'
        };
      }

      let data;
      try {
        data = JSON.parse(responseText);
      } catch (parseError) {
        return {
          success: false,
          error: 'Invalid JSON response from server'
        };
      }

      if (!data.success) {
        return {
          success: false,
          error: data.error || 'Failed to update employee profile'
        };
      }

      return {
        success: true,
        data: data.data || data.message
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  // MVV (Mission, Vision, Values) API functions
  async getMVV(token) {
    try {
      const response = await fetch(buildUrl('managebusiness', '/getmvv'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          sessionToken: token
        })
      });

      // Check if response is ok
      if (!response.ok) {
        return {
          success: false,
          error: `HTTP ${response.status}: ${response.statusText}`
        };
      }

      // Check if response has content
      const responseText = await response.text();
      if (!responseText) {
        return {
          success: false,
          error: 'Empty response from server'
        };
      }

      let data;
      try {
        data = JSON.parse(responseText);
      } catch (parseError) {
        return {
          success: false,
          error: 'Invalid JSON response from server'
        };
      }

      if (!data.success) {
        return {
          success: false,
          error: data.error || 'Failed to fetch MVV data'
        };
      }

      // Backend returns { success: true, data: { mission, vision, values } }
      const result = data.data || {
        mission: '',
        vision: '',
        values: ''
      };

      return {
        success: true,
        data: result
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  async editMVV(token, mission, vision, values) {
    try {
      // Prepare the request body - only include non-empty fields
      const requestBody = {
        sessionToken: token
      };
      
      if (mission && mission.trim()) {
        requestBody.mission = mission.trim();
      }
      
      if (vision && vision.trim()) {
        requestBody.vision = vision.trim();
      }
      
      if (values && values.trim()) {
        requestBody.values = values.trim();
      }

      const response = await fetch(buildUrl('managebusiness', '/editmvv'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(requestBody)
      });

      // Check if response is ok
      if (!response.ok) {
        return {
          success: false,
          error: `HTTP ${response.status}: ${response.statusText}`
        };
      }

      // Check if response has content
      const responseText = await response.text();
      if (!responseText) {
        return {
          success: false,
          error: 'Empty response from server'
        };
      }

      let data;
      try {
        data = JSON.parse(responseText);
      } catch (parseError) {
        return {
          success: false,
          error: 'Invalid JSON response from server'
        };
      }

      if (!data.success) {
        return {
          success: false,
          error: data.error || 'Failed to update MVV data'
        };
      }

      // CACHE INVALIDATION: Clear MVV cache on successful edit
      const businessId = getBusinessIdFromToken(token);
      if (businessId) {
        cache.invalidate('mvv', businessId);
      }

      return {
        success: true,
        data: data.data || 'MVV data updated successfully'
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  // Get HR Policies
  async getHRPolicies(token) {
    try {
      const response = await fetch(buildUrl('managebusiness', '/gethrpolicies'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          sessionToken: token  // Also send in body for backward compatibility
        })
      });

      // Check if response is ok
      if (!response.ok) {
        return {
          success: false,
          error: `HTTP ${response.status}: ${response.statusText}`
        };
      }

      // Check if response has content
      const responseText = await response.text();
      if (!responseText) {
        return {
          success: false,
          error: 'Empty response from server'
        };
      }

      let data;
      try {
        data = JSON.parse(responseText);
      } catch (parseError) {
        return {
          success: false,
          error: 'Invalid JSON response from server'
        };
      }

      if (!data.success) {
        return {
          success: false,
          error: data.error || 'Failed to fetch HR policies'
        };
      }

      return {
        success: true,
        data: data
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  // Upsert HR Policy (create or update)
  async upsertHRPolicy(token, policyName, policyFile, category, onProgress) {
    try {
      // Convert file to base64
      const base64Content = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          // Remove the data URL prefix (data:application/pdf;base64,)
          const base64 = reader.result.split(',')[1];
          resolve(base64);
        };
        reader.onerror = reject;
        reader.readAsDataURL(policyFile);
      });

      // Use XMLHttpRequest for upload progress tracking
      return new Promise((resolve, _reject) => {
        const xhr = new XMLHttpRequest();
        const url = buildUrl('managebusiness', '/upserthrpolicy');

        // Track upload progress
        xhr.upload.addEventListener('progress', (e) => {
          if (e.lengthComputable && onProgress) {
            const percentComplete = Math.round((e.loaded / e.total) * 100);
            onProgress(percentComplete);
          }
        });

        xhr.addEventListener('load', () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              const data = JSON.parse(xhr.responseText);

              // CACHE INVALIDATION: Clear HR policies cache on successful upload
              if (data.success) {
                const businessId = getBusinessIdFromToken(token);
                if (businessId) {
                  cache.invalidate('hrPolicies', businessId);
                }
              }

              resolve({
                success: data.success,
                data: data.data,
                error: data.error
              });
            } catch (parseError) {
              resolve({
                success: false,
                error: 'Invalid JSON response from server'
              });
            }
          } else {
            resolve({
              success: false,
              error: `HTTP ${xhr.status}: ${xhr.statusText}`
            });
          }
        });

        xhr.addEventListener('error', () => {
          resolve({
            success: false,
            error: 'Network error occurred'
          });
        });

        xhr.open('POST', url);
        xhr.setRequestHeader('Content-Type', 'application/json');
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        xhr.send(JSON.stringify({
          sessionToken: token,
          policyName: policyName,
          policyContent: base64Content
        }));
      });
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  // Delete HR Policy
  async deleteHRPolicy(token, policyName) {
    try {
      const response = await fetch(buildUrl('managebusiness', '/deletehrpolicy'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          sessionToken: token,
          policyName: policyName
        })
      });

      if (!response.ok) {
        return {
          success: false,
          error: `HTTP ${response.status}: ${response.statusText}`
        };
      }

      const responseText = await response.text();
      if (!responseText) {
        return {
          success: false,
          error: 'Empty response from server'
        };
      }

      let data;
      try {
        data = JSON.parse(responseText);
      } catch (parseError) {
        return {
          success: false,
          error: 'Invalid JSON response from server'
        };
      }

      if (!data.success) {
        return {
          success: false,
          error: data.error || 'Failed to delete policy'
        };
      }

      // CACHE INVALIDATION: Clear HR policies cache on successful delete
      const businessId = getBusinessIdFromToken(token);
      if (businessId) {
        cache.invalidate('hrPolicies', businessId);
      }

      return {
        success: true,
        data: data.data
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  // View HR Policy (returns blob for viewing/downloading)
  async viewHRPolicy(token, policyName) {
    try {
      const response = await fetch(buildUrl('managebusiness', '/viewhrpolicy'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          sessionToken: token,
          policyName: policyName
        })
      });

      if (!response.ok) {
        return {
          success: false,
          error: `HTTP ${response.status}: ${response.statusText}`
        };
      }

      // Backend returns JSON with downloadUrl
      const responseText = await response.text();
      if (!responseText) {
        return {
          success: false,
          error: 'Empty response from server'
        };
      }

      let data;
      try {
        data = JSON.parse(responseText);
      } catch (parseError) {
        return {
          success: false,
          error: 'Invalid JSON response from server'
        };
      }

      if (!data.success) {
        return {
          success: false,
          error: data.error || 'Failed to get policy'
        };
      }

      // Return the nested data
      return {
        success: true,
        data: data.data
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  // Get all employees for the business
  async getEmployees(token) {
    try {
      const response = await fetch(buildUrl('manageemployees', '/getemployees'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          sessionToken: token
        })
      });

      if (!response.ok) {
        return {
          success: false,
          error: `HTTP ${response.status}: ${response.statusText}`
        };
      }

      const data = await response.json();

      if (!data.success) {
        return {
          success: false,
          error: data.error || 'Failed to fetch employees'
        };
      }

      const employees = data.data || null;

      return {
        success: true,
        data: employees,
        summary: data.summary,
        leavePolicyGroups: data.leavePolicyGroups
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  // Get all workstations for the business
  async getWorkstations(token) {
    try {
      const response = await fetch(buildUrl('managebusiness', '/getworkstations'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          sessionToken: token
        })
      });

      if (!response.ok) {
        return {
          success: false,
          error: `HTTP error! status: ${response.status}`
        };
      }

      const data = await response.json();

      if (!data.success) {
        return {
          success: false,
          error: data.error || 'Failed to fetch workstations'
        };
      }

      return {
        success: true,
        data: data.data || []
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  // Add new workstation
  async addWorkstation(token, workstationName, workstationDescription, capacity = 1) {
    try {
      const response = await fetch(buildUrl('managebusiness', '/addworkstation'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          sessionToken: token,
          workstationName,
          workstationDescription,
          capacity
        })
      });

      if (!response.ok) {
        return {
          success: false,
          error: `HTTP error! status: ${response.status}`
        };
      }

      const data = await response.json();
      return data;
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  // Update workstation
  async updateWorkstation(token, workstationId, updates) {
    try {
      const response = await fetch(buildUrl('managebusiness', '/updateworkstation'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          sessionToken: token,
          workstationId,
          ...updates
        })
      });

      if (!response.ok) {
        return {
          success: false,
          error: `HTTP error! status: ${response.status}`
        };
      }

      const data = await response.json();
      return data;
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  // Delete workstation
  async deleteWorkstation(token, workstationId, permanent = false) {
    try {
      const response = await fetch(buildUrl('managebusiness', '/deleteworkstation'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          sessionToken: token,
          workstationId,
          permanent
        })
      });

      if (!response.ok) {
        return {
          success: false,
          error: `HTTP error! status: ${response.status}`
        };
      }

      const data = await response.json();
      return data;
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  // Edit employee profile (business - all profile fields)
  async editEmployeeProfileBusiness(token, employeeId, updateData) {
    try {
      const requestBody = {
        sessionToken: token,
        employeeId: employeeId,
        // Basic profile fields
        firstName: updateData.firstName,
        lastName: updateData.lastName,
        email: updateData.email,
        preferredName: updateData.preferredName,
        mobileNumber: updateData.mobileNumber,
        gender: updateData.gender,
        birthday: updateData.birthday,
        // Employment details
        jobTitle: updateData.jobTitle,
        startDate: updateData.startDate,
        jobDescription: updateData.jobDescription,
        leavePolicyGroupId: updateData.leavePolicyGroupId,
        // Compensation
        bonusMultiplier: updateData.bonusMultiplier,
        incentivePercentage: updateData.incentivePercentage,
        bonusPercentagePerQuarter: updateData.bonusPercentagePerQuarter,
        salary: updateData.salary,
        hourlyWage: updateData.hourlyWage,
        // Permissions
        accessBusinessAccount: updateData.accessBusinessAccount,
        accessBookingManagement: updateData.accessBookingManagement,
        accessPOS: updateData.accessPOS
      };

      const response = await fetch(buildUrl('manageemployees', '/editemployeeprofilebusiness'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        return {
          success: false,
          error: `HTTP ${response.status}: ${response.statusText}`
        };
      }

      const data = await response.json();

      // CACHE INVALIDATION: Clear employees cache on successful update
      if (data.success) {
        const businessId = getBusinessIdFromToken(token);
        if (businessId) {
          cache.invalidate('employees', businessId);
        }
      }

      return {
        success: data.success,
        data: data.success ? data.data : null,
        error: data.success ? null : (data.error || 'Failed to update compensation settings')
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  // Edit employee access (promote to manager)
  async editEmployeeAccess(token, employeeId, accessBusinessAccount) {
    try {
      const response = await fetch(buildUrl('manageemployees', '/editaccess'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          sessionToken: token,
          employeeId: employeeId,
          accessBusinessAccount: accessBusinessAccount
        })
      });

      if (!response.ok) {
        return {
          success: false,
          error: `HTTP ${response.status}: ${response.statusText}`
        };
      }

      const data = await response.json();
      return {
        success: data.success,
        data: data.success ? data.data : null,
        error: data.success ? null : (data.error || 'Failed to update employee access')
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  // Create reporting relationship (assign manager)
  async createReportRelation(token, reportingEmployeeId, managingEmployeeId) {
    try {
      const response = await fetch(buildUrl('manageemployees', '/createreportrelation'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          sessionToken: token,
          reportingEmployeeId: reportingEmployeeId,
          managingEmployeeId: managingEmployeeId
        })
      });

      if (!response.ok) {
        return {
          success: false,
          error: `HTTP ${response.status}: ${response.statusText}`
        };
      }

      const data = await response.json();
      return {
        success: data.success,
        data: data.success ? data.data : null,
        error: data.success ? null : (data.error || 'Failed to create reporting relationship')
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  // Delete reporting relationship (remove manager assignment)
  async deleteReportRelation(token, reportingEmployeeId, managingEmployeeId) {
    try {
      const response = await fetch(buildUrl('manageemployees', '/deletereportrelation'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          sessionToken: token,
          reportingEmployeeId: reportingEmployeeId,
          managingEmployeeId: managingEmployeeId
        })
      });

      if (!response.ok) {
        return {
          success: false,
          error: `HTTP ${response.status}: ${response.statusText}`
        };
      }

      const data = await response.json();
      return {
        success: data.success,
        data: data.success ? data.data : null,
        error: data.success ? null : (data.error || 'Failed to delete reporting relationship')
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  // Delete employee from business
  async deleteEmployee(token, employeeId) {
    try {
      const response = await fetch(buildUrl('manageemployees', '/deleteemployee'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          sessionToken: token,
          employeeId: employeeId
        })
      });

      const data = await response.json();
      return {
        success: data.success,
        data: data.success ? data : null,
        error: data.success ? null : (data.error || 'Failed to delete employee')
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  // Goal Tracking API methods
  async createGoalEntry(token, entryData) {
    try {
      const response = await fetch(buildUrl('trackgoals', '/createentry'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionToken: token,
          date: entryData.date,
          totalNumberOfClients: entryData.totalNumberOfClients,
          numberOfClientsRebooked: entryData.numberOfClientsRebooked,
          numberOfNewClients: entryData.numberOfNewClients,
          numberOfRetailUnitsSold: entryData.numberOfRetailUnitsSold,
          treatmentSales: entryData.treatmentSales,
          retailSales: entryData.retailSales
        })
      });
      const data = await response.json();
      return {
        success: data.success,
        data: data.success ? data.data : null,
        error: data.success ? null : mapGoalTrackingError(data.error)
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  async getSingleGoalEntry(token, goalTrackingEntryId) {
    try {
      const response = await fetch(buildUrl('trackgoals', '/getsingleentry'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionToken: token, goalTrackingEntryId })
      });
      const data = await response.json();
      return {
        success: data.success,
        data: data.success ? data.data : null,
        error: data.success ? null : mapGoalTrackingError(data.error)
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  async getGoalEntries(token, userId, fromDate, toDate) {
    try {
      const response = await fetch(buildUrl('trackgoals', '/getmultipleentries'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionToken: token, userId, fromDate, toDate })
      });
      const data = await response.json();
      return {
        success: data.success,
        data: data.success ? data.data : null,
        error: data.success ? null : mapGoalTrackingError(data.error)
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  async getBusinessGoalSummary(token, fromDate, toDate) {
    try {
      const response = await fetch(buildUrl('trackgoals', '/getbusinessgoalsummary'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionToken: token, fromDate, toDate })
      });
      const data = await response.json();
      return {
        success: data.success,
        data: data.success ? data.data : null,
        error: data.success ? null : mapGoalTrackingError(data.error)
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  async editGoalEntry(token, goalTrackingEntryId, updates) {
    try {
      const response = await fetch(buildUrl('trackgoals', '/editentry'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionToken: token,
          goalTrackingEntryId,
          ...updates
        })
      });
      const data = await response.json();
      return {
        success: data.success,
        data: data.success ? data.data : null,
        error: data.success ? null : mapGoalTrackingError(data.error)
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  async autoPopulateFromBookings(token, date, userId) {
    try {
      const response = await fetch(buildUrl('trackgoals', '/autopopulatefrombookings'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionToken: token, date, UserId: userId })
      });
      const data = await response.json();
      return {
        success: data.success,
        data: data.success ? data.data : null,
        error: data.success ? null : mapGoalTrackingError(data.error)
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  async getCurrentIncentive(token, userId) {
    try {
      const response = await fetch(buildUrl('trackgoals', '/getcurrentincentive'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionToken: token, UserId: userId })
      });
      const data = await response.json();
      return {
        success: data.success,
        data: data.success ? data : null,  // Backend returns data at root level
        error: data.success ? null : mapGoalTrackingError(data.error)
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  async getBonusLevel(token, userId) {
    try {
      const response = await fetch(buildUrl('trackgoals', '/getbonuslevel'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionToken: token, UserId: userId })
      });
      const data = await response.json();
      return {
        success: data.success,
        data: data.success ? data.data : null,
        error: data.success ? null : mapGoalTrackingError(data.error)
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  async getQuarterlyBonus(token, userId) {
    try {
      const response = await fetch(buildUrl('trackgoals', '/getquarterlybonus'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionToken: token, UserId: userId })
      });
      const data = await response.json();
      return {
        success: data.success,
        data: data.success ? data.data : null,
        error: data.success ? null : mapGoalTrackingError(data.error)
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  // Update employee targets and commission settings
  async updateEmployeeTargets(token, employeeId, targets, workSchedule = null, commissionSettings = null) {
    try {
      const requestBody = {
        sessionToken: token,
        employeeId: employeeId,
        targets: targets
      };

      if (workSchedule) {
        requestBody.workSchedule = workSchedule;
      }

      if (commissionSettings) {
        requestBody.commissionSettings = commissionSettings;
      }

      const response = await fetch(buildUrl('manageemployees', '/updateemployeetargets'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody)
      });

      const data = await response.json();

      // CACHE INVALIDATION: Clear employees cache on successful update
      if (data.success) {
        const businessId = getBusinessIdFromToken(token);
        if (businessId) {
          cache.invalidate('employees', businessId);
        }
      }

      return {
        success: data.success,
        data: data.success ? data.data : null,
        error: data.success ? null : (data.error || 'Failed to update employee targets')
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  // Self-service variant — employee updates their own targets + workSchedule.
  // employeeId is derived server-side from the session token.
  async updateEmployeeTargetsSelf(token, targets, workSchedule = null) {
    try {
      const requestBody = { sessionToken: token };
      if (targets) requestBody.targets = targets;
      if (workSchedule) requestBody.workSchedule = workSchedule;

      const response = await fetch(buildUrl('manageemployees', '/updateemployeetargetsself'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody)
      });

      const data = await response.json();
      return {
        success: data.success,
        data: data.success ? data.data : null,
        error: data.success ? null : (data.error || 'Failed to update your targets')
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  // CHECKLIST MANAGEMENT API METHODS
  async getChecklistInstances(token, userId = null, status = null, fromDate = null, toDate = null) {
    try {
      const requestBody = { sessionToken: token };
      if (userId) requestBody.userId = userId;
      if (status) requestBody.status = status;
      if (fromDate) requestBody.fromDate = fromDate;
      if (toDate) requestBody.toDate = toDate;

      const response = await fetch(buildUrl('managechecklist', '/getchecklistinstances'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody)
      });

      const data = await response.json();
      return {
        success: data.success,
        data: data.success ? data.data : null,
        summary: data.success ? data.summary : null,
        error: data.success ? null : (data.error || 'Failed to fetch checklist instances')
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  async getChecklist(token, checklistId) {
    try {
      const response = await fetch(buildUrl('managechecklist', '/getchecklist'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionToken: token, checklistId })
      });

      const data = await response.json();
      // Backend returns checklist properties directly (checklistId, title, content, etc.)
      // not wrapped in a data property
      return {
        success: data.success,
        ...data, // Spread all properties from backend response
        error: data.success ? null : (data.error || 'Failed to fetch checklist')
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  async startChecklistInstance(token, instanceId) {
    try {
      const response = await fetch(buildUrl('managechecklist', '/startchecklistinstance'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionToken: token, instanceId })
      });

      const data = await response.json();
      // Backend returns instance properties directly (instanceId, content, etc.)
      // not wrapped in a data property
      return {
        success: data.success,
        ...data, // Spread all properties from backend response
        error: data.success ? null : (data.error || 'Failed to start checklist instance')
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  async submitChecklistInstance(token, instanceId, submissionData) {
    try {
      const response = await fetch(buildUrl('managechecklist', '/submitchecklistinstance'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionToken: token, instanceId, submissionData })
      });

      const data = await response.json();
      return {
        success: data.success,
        data: data.success ? data.data : null,
        error: data.success ? null : (data.error || 'Failed to submit checklist')
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  async updateChecklistProgress(token, instanceId, progressData) {
    try {
      const response = await fetch(buildUrl('managechecklist', '/updatechecklistprogress'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionToken: token, instanceId, progressData })
      });

      const data = await response.json();
      return {
        success: data.success,
        data: data.success ? data.data : null,
        error: data.success ? null : (data.error || 'Failed to update progress')
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  async getChecklistPerformance(token, userId = null, fromDate = null, toDate = null) {
    try {
      const requestBody = { sessionToken: token };
      if (userId) requestBody.userId = userId;
      if (fromDate) requestBody.fromDate = fromDate;
      if (toDate) requestBody.toDate = toDate;

      const response = await fetch(buildUrl('managechecklist', '/getchecklistperformance'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody)
      });

      const data = await response.json();
      return {
        success: data.success,
        data: data.success ? data.data : null,
        error: data.success ? null : (data.error || 'Failed to fetch performance data')
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  async getChecklists(token) {
    try {
      const response = await fetch(buildUrl('managechecklist', '/getchecklists'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionToken: token })
      });

      const data = await response.json();
      return {
        success: data.success,
        data: data.success ? data.data : null,
        error: data.success ? null : (data.error || 'Failed to fetch checklists')
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  async createChecklist(token, title, description, content) {
    try {
      const response = await fetch(buildUrl('managechecklist', '/createchecklist'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionToken: token,
          title,
          description,
          content: typeof content === 'string' ? content : JSON.stringify(content)
        })
      });

      const data = await response.json();
      return {
        success: data.success,
        data: data.success ? data : null,
        error: data.success ? null : (data.error || 'Failed to create checklist')
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  async editChecklist(token, checklistId, title, description, content) {
    try {
      const response = await fetch(buildUrl('managechecklist', '/editchecklist'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionToken: token,
          checklistId,
          title,
          description,
          content: typeof content === 'string' ? content : JSON.stringify(content)
        })
      });

      const data = await response.json();
      return {
        success: data.success,
        data: data.success ? data : null,
        error: data.success ? null : (data.error || 'Failed to edit checklist')
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  async deleteChecklist(token, checklistId) {
    try {
      const response = await fetch(buildUrl('managechecklist', '/deletechecklist'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionToken: token,
          checklistId
        })
      });

      const data = await response.json();
      return {
        success: data.success,
        data: data.success ? data : null,
        error: data.success ? null : (data.error || 'Failed to delete checklist')
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  async createChecklistSchedule(token, checklistId, scheduleName, frequency, dayOfWeek, dayOfMonth, assignedUserIds) {
    try {
      const response = await fetch(buildUrl('managechecklist', '/createchecklistschedule'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionToken: token,
          checklistId,
          scheduleName,
          frequency,
          dayOfWeek,
          dayOfMonth,
          assignedUserIds
        })
      });

      const data = await response.json();
      return {
        success: data.success,
        data: data.success ? data : null,
        error: data.success ? null : (data.error || 'Failed to create schedule')
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  async getChecklistSchedules(token, checklistId = null) {
    try {
      const requestBody = { sessionToken: token };
      if (checklistId) requestBody.checklistId = checklistId;

      const response = await fetch(buildUrl('managechecklist', '/getchecklistschedules'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody)
      });

      const data = await response.json();
      return {
        success: data.success,
        data: data.success ? data.data : null,
        error: data.success ? null : (data.error || 'Failed to fetch schedules')
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  async editChecklistSchedule(token, scheduleId, scheduleName, frequency, dayOfWeek, dayOfMonth) {
    try {
      const response = await fetch(buildUrl('managechecklist', '/editchecklistschedule'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionToken: token,
          scheduleId,
          scheduleName,
          frequency,
          dayOfWeek,
          dayOfMonth
        })
      });

      const data = await response.json();
      return {
        success: data.success,
        data: data.success ? data : null,
        error: data.success ? null : (data.error || 'Failed to edit schedule')
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  async assignUsersToSchedule(token, scheduleId, assignedUserIds) {
    try {
      const response = await fetch(buildUrl('managechecklist', '/assignuserstoschedule'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionToken: token,
          scheduleId,
          assignedUserIds
        })
      });

      const data = await response.json();
      return {
        success: data.success,
        data: data.success ? data : null,
        error: data.success ? null : (data.error || 'Failed to assign users')
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  async deleteChecklistSchedule(token, scheduleId) {
    try {
      const response = await fetch(buildUrl('managechecklist', '/deletechecklistschedule'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionToken: token,
          scheduleId
        })
      });

      const data = await response.json();
      return {
        success: data.success,
        data: data.success ? data : null,
        error: data.success ? null : (data.error || 'Failed to delete schedule')
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  // NOTIFICATION API METHODS
  async getNotifications(token, unreadOnly = false) {
    try {
      const response = await fetch(buildUrl('managechecklist', '/getnotifications'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionToken: token, unreadOnly })
      });

      const data = await response.json();
      return {
        success: data.success,
        data: data.success ? data.data : [],
        unreadCount: data.success ? data.unreadCount : 0,
        error: data.success ? null : (data.error || 'Failed to fetch notifications')
      };
    } catch (error) {
      return {
        success: false,
        data: [],
        unreadCount: 0,
        error: error.message || 'Network error occurred'
      };
    }
  },

  async markNotificationAsRead(token, notificationId) {
    try {
      const response = await fetch(buildUrl('managechecklist', '/marknotificationasread'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionToken: token, notificationId })
      });

      const data = await response.json();
      return {
        success: data.success,
        error: data.success ? null : (data.error || 'Failed to mark notification as read')
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  async dismissNotification(token, notificationId) {
    try {
      const response = await fetch(buildUrl('managechecklist', '/dismissnotification'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionToken: token, notificationId })
      });

      const data = await response.json();
      return {
        success: data.success,
        error: data.success ? null : (data.error || 'Failed to dismiss notification')
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  async createManualInstance(token, checklistId, userId, instanceDate, dueTime) {
    try {
      const response = await fetch(buildUrl('managechecklist', '/createmanualinstance'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionToken: token,
          checklistId,
          userId,
          instanceDate,
          dueTime
        })
      });

      const data = await response.json();
      return {
        success: data.success,
        data: data.success ? data : null,
        error: data.success ? null : (data.error || 'Failed to create manual instance')
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  async updateChecklistInstance(token, instanceId, instanceDate, dueTime, userId) {
    try {
      const response = await fetch(buildUrl('managechecklist', '/updatechecklistinstance'), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionToken: token,
          instanceId,
          instanceDate,
          dueTime,
          userId
        })
      });

      const data = await response.json();
      return {
        success: data.success,
        data: data.success ? data : null,
        error: data.success ? null : (data.error || 'Failed to update checklist instance')
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  async deleteChecklistInstance(token, instanceId) {
    try {
      const response = await fetch(buildUrl('managechecklist', '/deletechecklistinstance'), {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionToken: token,
          instanceId
        })
      });

      const data = await response.json();
      return {
        success: data.success,
        data: data.success ? data : null,
        error: data.success ? null : (data.error || 'Failed to delete checklist instance')
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  async triggerScheduleGeneration(token) {
    try {
      const response = await fetch(buildUrl('managechecklist', '/triggerschedulegeneration'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionToken: token
        })
      });

      const data = await response.json();
      return {
        success: data.success,
        data: data.success ? data : null,
        error: data.success ? null : (data.error || 'Failed to trigger schedule generation')
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  async getPendingReviews(token) {
    try {
      const response = await fetch(buildUrl('managechecklist', '/getpendingreviews'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionToken: token })
      });

      const data = await response.json();
      return {
        success: data.success,
        data: data.success ? data.data : null,
        count: data.success ? data.count : 0,
        error: data.success ? null : (data.error || 'Failed to fetch pending reviews')
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  async reviewChecklistSubmission(token, submissionId, taskScores, totalScore, comments) {
    try {
      const response = await fetch(buildUrl('managechecklist', '/reviewchecklistsubmission'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionToken: token,
          submissionId,
          taskScores,
          totalScore,
          comments
        })
      });

      const data = await response.json();
      return {
        success: data.success,
        data: data.success ? data : null,
        error: data.success ? null : (data.error || 'Failed to submit review')
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  // BOOKING MANAGEMENT API METHODS
  async getBusinessBookings(token, fromDate, toDate, status, clientId, pageSize) {
    try {
      const url = buildUrl('managebookings', '/getbusinessbookings');

      const requestBody = {
        fromDate: fromDate,
        toDate: toDate,
        status: status,
        clientId: clientId,
        pageSize: pageSize
      };

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      const data = await response.json();

      const result = {
        success: data.success,
        data: data.success ? (data.data?.bookings || data.bookings) : null,
        error: data.success ? null : (data.error || 'Failed to fetch bookings')
      };
      return result;
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  async getEmployeeCalendar(token, employeeId, fromDate, toDate) {
    try {
      const response = await fetch(buildUrl('managebookings', '/getemployeecalendar'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          employeeId: employeeId,
          fromDate: fromDate,
          toDate: toDate
        })
      });

      const data = await response.json();
      return {
        success: data.success,
        data: data.success ? (data.data?.bookings || data.bookings) : null,
        error: data.success ? null : (data.error || 'Failed to fetch employee calendar')
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  async createBooking(token, bookingData) {
    try {
      const response = await fetch(buildUrl('managebookings', '/createbooking'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(bookingData)
      });

      const data = await response.json();
      return {
        success: data.success,
        data: data.success ? (data.data?.booking || data.booking) : null,
        error: data.success ? null : (data.error || 'Failed to create booking')
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  async updateBooking(token, bookingId, updates) {
    try {
      const response = await fetch(buildUrl('managebookings', '/updatebooking'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          bookingId: bookingId,
          ...updates
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        return {
          success: false,
          error: `Server error: ${response.status} - ${errorText || 'Internal server error'}`
        };
      }

      const data = await response.json();
      return {
        success: data.success,
        data: data.success ? (data.data?.booking || data.booking) : null,
        error: data.success ? null : (data.error || 'Failed to update booking')
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  async updateEmployeeBookingNotes(token, bookingId, notes) {
    try {
      const response = await fetch(buildUrl('managebookings', '/updateemployeebookingnotes'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          bookingId: bookingId,
          notes: notes
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        return {
          success: false,
          error: `Server error: ${response.status} - ${errorText || 'Internal server error'}`
        };
      }

      const data = await response.json();
      return {
        success: data.success,
        data: data.success ? data.data : null,
        error: data.success ? null : (data.error || 'Failed to update booking notes')
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  async updateBookingStatus(token, bookingId, newStatus) {
    try {
      const response = await fetch(buildUrl('managebookings', '/updatebookingstatus'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          bookingId: bookingId,
          status: newStatus
        })
      });

      const data = await response.json();
      return {
        success: data.success,
        data: data.success ? (data.data || data) : null,
        error: data.success ? null : (data.error || 'Failed to update booking status')
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  async deleteBooking(token, bookingId) {
    try {
      const response = await fetch(buildUrl('managebookings', '/deletebooking'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          bookingId: bookingId
        })
      });

      const data = await response.json();
      return {
        success: data.success,
        data: data.success ? (data.data || data) : null,
        error: data.success ? null : (data.error || 'Failed to delete booking')
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  async getBookingDetails(token, bookingId) {
    try {
      const response = await fetch(buildUrl('managebookings', '/getbookingdetails'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          bookingId: bookingId
        })
      });

      const data = await response.json();
      return {
        success: data.success,
        data: data.success ? (data.data?.booking || data.booking) : null,
        error: data.success ? null : (data.error || 'Failed to fetch booking details')
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },


  async addServiceToBooking(token, bookingId, serviceData) {
    try {
      const response = await fetch(buildUrl('managebookings', '/addservicetobooking'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          bookingId: bookingId,
          ...serviceData
        })
      });

      const data = await response.json();
      return {
        success: data.success,
        data: data.success ? (data.data || data) : null,
        error: data.success ? null : (data.error || 'Failed to add service to booking')
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  async removeServiceFromBooking(token, bookingServiceId) {
    try {
      const response = await fetch(buildUrl('managebookings', '/removeservicefrombooking'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          bookingServiceId: bookingServiceId
        })
      });

      const data = await response.json();
      return {
        success: data.success,
        data: data.success ? (data.data || data) : null,
        error: data.success ? null : (data.error || 'Failed to remove service from booking')
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  async updateBookingServices(token, bookingId, services) {
    try {
      const response = await fetch(buildUrl('managebookings', '/updatebookingservices'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          bookingId: bookingId,
          services: services
        })
      });

      const data = await response.json();
      return {
        success: data.success,
        data: data.success ? (data.data?.services || data.services) : null,
        error: data.success ? null : (data.error || 'Failed to update booking services')
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  async getAvailableEmployees(token, serviceId, bookingDate, duration) {
    try {
      const response = await fetch(buildUrl('managebookings', '/getavailableemployees'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          serviceId: serviceId,
          bookingDate: bookingDate,
          duration: duration
        })
      });

      const data = await response.json();
      return {
        success: data.success,
        data: data.success ? (data.data?.availableEmployees || data.availableEmployees || data.employees) : null,
        error: data.success ? null : (data.error || 'Failed to fetch available employees')
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  async getEmployeeBookingsInRange(token, userId, startDate, endDate) {
    try {
      const response = await fetch(buildUrl('managebookings', '/getemployeebookingsinrange'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          userId: userId,
          startDate: startDate,
          endDate: endDate
        })
      });

      const data = await response.json();
      return {
        success: data.success,
        data: data.success ? data.data : null,
        error: data.success ? null : (data.error || 'Failed to fetch employee bookings')
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  async getClientBookingHistory(token, clientId) {
    try {
      const response = await fetch(buildUrl('managebookings', '/getclientbookinghistory'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          clientId: clientId
        })
      });

      const data = await response.json();

      const result = {
        success: data.success,
        data: data.success ? data.bookingHistory : null,
        error: data.success ? null : (data.error || 'Failed to fetch client booking history')
      };
      return result;
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  async validateBookingConflicts(token, bookingDate, duration, userIds, bookingId = null, workstationIds = null) {
    try {
      const body = {
        bookingDate: bookingDate,
        duration: duration,
        userIds: userIds,
        bookingId: bookingId
      };
      if (workstationIds && workstationIds.length > 0) {
        body.workstationIds = workstationIds;
      }

      const response = await fetch(buildUrl('managebookings', '/validatebookingconflicts'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });

      const data = await response.json();
      return {
        success: data.success,
        details: data.success ? (data.details || data.data?.details || []) : [],
        hasConflicts: data.success ? (data.hasConflicts || data.data?.hasConflicts || false) : false,
        conflictCount: data.success ? (data.conflictCount || data.data?.conflictCount || 0) : 0,
        error: data.success ? null : (data.error || 'Failed to validate booking conflicts')
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  // Get workstation availability for a given time slot
  async getWorkstationAvailability(token, bookingDate, duration, bookingId = null) {
    try {
      const response = await fetch(buildUrl('managebookings', '/getworkstationavailability'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          bookingDate,
          duration,
          bookingId
        })
      });

      const data = await response.json();
      return {
        success: data.success,
        data: data.success ? (data.data || []) : [],
        error: data.success ? null : (data.error || 'Failed to get workstation availability')
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  // ==================== MANAGE CLIENTS API ====================

  async getClients(token, searchTerm = null, isActive = null, pageSize = null, pageNumber = null) {
    // Retry logic for cold starts
    let lastError;
    for (let attempt = 0; attempt < 2; attempt++) {
      try {
        const timeout = attempt === 0 ? 8000 : 30000; // 8s first try, 30s on retry

        const response = await fetchWithTimeout(buildUrl('manageclients', '/getclients'), {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({
            sessionToken: token,
            searchTerm,
            isActive,
            pageSize,
            pageNumber
          })
        }, timeout);

        const data = await response.json();

        return {
          success: data.success,
          data: data.success ? (data.data || data.clients || []) : null,
          error: data.success ? null : (data.error || 'Failed to fetch clients')
        };
      } catch (error) {
        lastError = error;
        if (attempt === 0 && error.message.includes('timeout')) {
          continue;
        }
        break;
      }
    }

    return {
      success: false,
      error: lastError?.message || 'Network error occurred'
    };
  },

  async getClient(token, clientId) {
    try {
      const response = await fetch(buildUrl('manageclients', '/getclient'), {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          clientId
        })
      });

      const data = await response.json();
      return {
        success: data.success,
        data: data.success ? data.data : null,
        error: data.success ? null : (data.error || 'Failed to fetch client')
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  async createClient(token, clientData) {
    try {
      const response = await fetch(buildUrl('manageclients', '/createclient'), {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...clientData
        })
      });

      const data = await response.json();

      return {
        success: data.success,
        data: data.success ? (data.data || data.client) : null,
        error: data.success ? null : (data.error || 'Failed to create client')
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  async updateClient(token, clientId, clientData) {
    try {
      const response = await fetch(buildUrl('manageclients', '/updateclient'), {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          clientId,
          ...clientData
        })
      });

      const data = await response.json();
      return {
        success: data.success,
        data: data.success ? data.data : null,
        error: data.success ? null : (data.error || 'Failed to update client')
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  async deactivateClient(token, clientId) {
    try {
      const response = await fetch(buildUrl('manageclients', '/deactivateclient'), {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionToken: token,
          clientId: clientId
        })
      });

      const data = await response.json();

      return {
        success: data.success,
        data: data.success ? data.data : null,
        error: data.success ? null : (data.error || 'Failed to deactivate client')
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  async reactivateClient(token, clientId) {
    try {
      const response = await fetch(buildUrl('manageclients', '/reactivateclient'), {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionToken: token,
          clientId
        })
      });

      const data = await response.json();
      return {
        success: data.success,
        data: data.success ? data.data : null,
        error: data.success ? null : (data.error || 'Failed to reactivate client')
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  async searchClients(token, searchTerm, searchType = 'name') {
    try {
      const response = await fetch(buildUrl('manageclients', '/searchclients'), {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          searchTerm,
          searchType
        })
      });

      const data = await response.json();
      return {
        success: data.success,
        data: data.success ? data.searchResults : null,
        error: data.success ? null : (data.error || 'Failed to search clients')
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  async getClientBookingSummary(token, clientId) {
    // Retry logic for cold starts
    let lastError;
    for (let attempt = 0; attempt < 2; attempt++) {
      try {
        const timeout = attempt === 0 ? 8000 : 30000; // 8s first try, 30s on retry

        const response = await fetchWithTimeout(buildUrl('manageclients', '/getclientbookingsummary'), {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({
            sessionToken: token,
            clientId
          })
        }, timeout);

        const data = await response.json();

        return {
          success: data.success,
          data: data.success ? data.bookingSummary : null,
          error: data.success ? null : (data.error || 'Failed to fetch booking summary')
        };
      } catch (error) {
        lastError = error;
        if (attempt === 0 && error.message.includes('timeout')) {
          continue;
        }
        break;
      }
    }

    return {
      success: false,
      error: lastError?.message || 'Network error occurred'
    };
  },

  async getClientsByBirthday(token, month = null) {
    try {
      const response = await fetch(buildUrl('manageclients', '/getclientsbybirthday'), {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionToken: token,
          month
        })
      });

      const data = await response.json();
      return {
        success: data.success,
        // Backend returns 'clients' not 'data'
        data: data.success ? data.clients : null,
        clientsByMonth: data.success ? data.clientsByMonth : null,
        summary: data.success ? data.summary : null,
        error: data.success ? null : (data.error || 'Failed to fetch birthday clients')
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  async getNewClients(token, fromDate = null, toDate = null) {
    try {
      const response = await fetch(buildUrl('manageclients', '/getnewclients'), {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionToken: token,
          fromDate,
          toDate
        })
      });

      const data = await response.json();
      return {
        success: data.success,
        data: data.success ? {
          newClients: data.newClients || [],
          clientsByDate: data.clientsByDate || {},
          summary: data.summary || {},
          generatedAt: data.generatedAt
        } : null,
        error: data.success ? null : (data.error || 'Failed to fetch new clients')
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  async getClientRetention(token, fromDate = null, toDate = null) {
    try {
      const response = await fetch(buildUrl('manageclients', '/getclientretention'), {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionToken: token,
          fromDate,
          toDate
        })
      });

      const data = await response.json();
      return {
        success: data.success,
        // Backend returns 'data' (clients array), 'clientsByRisk', 'summary', 'reEngagementTargets'
        data: data.success ? data.data : null,
        clientsByRisk: data.success ? data.clientsByRisk : null,
        summary: data.success ? data.summary : null,
        reEngagementTargets: data.success ? data.reEngagementTargets : null,
        error: data.success ? null : (data.error || 'Failed to fetch retention data')
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  async getClientNotes(token, clientId) {
    try {
      const response = await fetch(buildUrl('manageclients', '/getclientnotes'), {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionToken: token,
          clientId
        })
      });

      const data = await response.json();
      return {
        success: data.success,
        data: data.success ? data : null,
        error: data.success ? null : (data.error || 'Failed to fetch client notes')
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  async updateClientNotes(token, clientId, notes) {
    try {
      const response = await fetch(buildUrl('manageclients', '/updateclientnotes'), {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionToken: token,
          clientId,
          notes
        })
      });

      const data = await response.json();
      return {
        success: data.success,
        data: data.success ? data.data : null,
        error: data.success ? null : (data.error || 'Failed to update notes')
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  async addClientNote(token, clientId, noteContent) {
    try {
      const response = await fetch(buildUrl('manageclients', '/addclientnote'), {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionToken: token,
          clientId,
          noteContent
        })
      });

      const data = await response.json();
      return {
        success: data.success,
        data: data.success ? data : null,
        error: data.success ? null : (data.error || 'Failed to add note')
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  async deleteClientNote(token, noteId) {
    try {
      const response = await fetch(buildUrl('manageclients', '/deleteclientnote'), {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionToken: token,
          noteId
        })
      });

      const data = await response.json();
      return {
        success: data.success,
        data: data.success ? data : null,
        error: data.success ? null : (data.error || 'Failed to delete note')
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  async markClientContacted(token, clientId, contacted) {
    try {
      const response = await fetch(buildUrl('manageclients', '/markclientcontacted'), {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionToken: token,
          clientId,
          contacted
        })
      });

      const data = await response.json();
      return {
        success: data.success,
        data: data.success ? data : null,
        error: data.success ? null : (data.error || 'Failed to update contact status')
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  async getClientPreferences(token, clientId) {
    try {
      const response = await fetch(buildUrl('manageclients', '/getclientpreferences'), {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionToken: token,
          clientId
        })
      });

      const data = await response.json();
      return {
        success: data.success,
        data: data.success ? data.data : null,
        error: data.success ? null : (data.error || 'Failed to fetch preferences')
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  // Business Settings
  async getBusinessSettings(token) {
    try {
      const response = await fetch(buildUrl('managebusiness', '/getbusinesssettings'), {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionToken: token
        })
      });

      const data = await response.json();
      return {
        success: data.success,
        data: data.success ? data : null,
        error: data.success ? null : (data.error || 'Failed to get business settings')
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  async updateBusinessSettings(token, settings) {
    try {
      const response = await fetch(buildUrl('managebusiness', '/updatebusinesssettings'), {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionToken: token,
          ...settings
        })
      });

      const data = await response.json();
      return {
        success: data.success,
        data: data.success ? data : null,
        error: data.success ? null : (data.error || 'Failed to update business settings')
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  async exportClients(token, format = 'json', filterActive = null) {
    try {
      // Backend expects uppercase format ('CSV' or 'JSON')
      const upperFormat = format.toUpperCase();

      const requestBody = {
        sessionToken: token,
        format: upperFormat,
        filterActive
      };

      const response = await fetch(buildUrl('manageclients', '/exportclients'), {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody)
      });

      const data = await response.json();

      return {
        success: data.success,
        data: data.success ? (data.data || data.exportData) : null,
        error: data.success ? null : (data.error || 'Failed to export clients')
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  async validateClientData(token, clientData) {
    try {
      const response = await fetch(buildUrl('manageclients', '/validateclientdata'), {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          clientData
        })
      });

      const data = await response.json();
      return {
        success: data.success,
        data: data.success ? data.data : null,
        error: data.success ? null : (data.error || 'Validation failed')
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  async getClientAnalytics(token, fromDate = null, toDate = null) {
    try {
      const response = await fetch(buildUrl('manageclients', '/getclientanalytics'), {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionToken: token,
          fromDate,
          toDate
        })
      });

      const data = await response.json();
      return {
        success: data.success,
        data: data.success ? data.data : null,
        error: data.success ? null : (data.error || 'Failed to fetch analytics')
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  // Legacy method - kept for backwards compatibility
  async getBusinessClients(token) {
    return this.getClients(token);
  },

  async getBusinessServices(token) {
    try {
      const response = await fetch(buildUrl('manageservices', '/getservices'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({})
      });

      const data = await response.json();
      return {
        success: data.success,
        data: data.success ? (data.data?.services || data.services) : null,
        error: data.success ? null : (data.error || 'Failed to fetch services')
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  // ManagePictures API functions
  async uploadProfilePicture(token, imageData, targetUserId = null) {
    try {
      let body;
      let headers = {
        'Authorization': `Bearer ${token}`
      };

      if (imageData instanceof FormData) {
        body = imageData;
      } else {
        headers['Content-Type'] = 'application/json';
        body = JSON.stringify({ image: imageData });
      }

      const response = await fetch(buildUrl('managepictures', '/uploadprofilepicture') + (targetUserId ? `?targetUserId=${targetUserId}` : ''), {
        method: 'POST',
        headers: headers,
        body: body
      });

      if (!response.ok) {
        return {
          success: false,
          error: `HTTP ${response.status}: ${response.statusText}`
        };
      }

      const data = await response.json();
      return {
        success: data.success,
        data: data.success ? data.data : null,
        error: data.success ? null : (data.error || 'Failed to upload profile picture')
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  async getProfilePicture(token, userId = null) {
    try {
      const response = await fetch(buildUrl('managepictures', '/getprofilepicture') + (userId ? `?userId=${userId}` : ''), {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        if (response.status === 404) {
          return {
            success: false,
            error: 'Profile picture not found',
            notFound: true
          };
        }
        return {
          success: false,
          error: `HTTP ${response.status}: ${response.statusText}`
        };
      }

      const blob = await response.blob();
      const imageUrl = URL.createObjectURL(blob);

      return {
        success: true,
        data: {
          imageUrl: imageUrl,
          contentType: response.headers.get('Content-Type')
        },
        error: null
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  async deleteProfilePicture(token, userId = null) {
    try {
      const response = await fetch(buildUrl('managepictures', '/deleteprofilepicture') + (userId ? `?userId=${userId}` : ''), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        if (response.status === 404) {
          return {
            success: false,
            error: 'Profile picture not found',
            notFound: true
          };
        }
        return {
          success: false,
          error: `HTTP ${response.status}: ${response.statusText}`
        };
      }

      const data = await response.json();
      return {
        success: data.success,
        data: data.success ? data.data : null,
        error: data.success ? null : (data.error || 'Failed to delete profile picture')
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  // Business Logo functions
  async uploadBusinessLogo(token, imageData) {
    try {
      // Support both FormData and base64 string for backward compatibility
      let body;
      let headers = {
        'Authorization': `Bearer ${token}`
      };

      if (imageData instanceof FormData) {
        // FormData - let browser set Content-Type with boundary
        body = imageData;
      } else {
        // Base64 string (legacy support)
        headers['Content-Type'] = 'application/json';
        body = JSON.stringify({ image: imageData });
      }

      const response = await fetch(buildUrl('managepictures', '/uploadbusinesslogo'), {
        method: 'POST',
        headers: headers,
        body: body
      });

      if (!response.ok) {
        return {
          success: false,
          error: `HTTP ${response.status}: ${response.statusText}`
        };
      }

      const data = await response.json();
      return {
        success: data.success,
        data: data.success ? data.data : null,
        error: data.success ? null : (data.error || 'Failed to upload business logo')
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  async getBusinessLogo(token, businessId = null) {
    try {
      const url = buildUrl('managepictures', '/getbusinesslogo') + (businessId ? `?businessId=${businessId}` : '');
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        if (response.status === 404) {
          return {
            success: false,
            error: 'Business logo not found',
            notFound: true
          };
        }
        return {
          success: false,
          error: `HTTP ${response.status}: ${response.statusText}`
        };
      }

      const blob = await response.blob();
      const imageUrl = URL.createObjectURL(blob);

      return {
        success: true,
        data: {
          imageUrl: imageUrl,
          contentType: response.headers.get('Content-Type')
        },
        error: null
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  async deleteBusinessLogo(token) {
    try {
      const response = await fetch(buildUrl('managepictures', '/deletebusinesslogo'), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        if (response.status === 404) {
          return {
            success: false,
            error: 'Business logo not found',
            notFound: true
          };
        }
        return {
          success: false,
          error: `HTTP ${response.status}: ${response.statusText}`
        };
      }

      const data = await response.json();
      return {
        success: data.success,
        data: data.success ? data.data : null,
        error: data.success ? null : (data.error || 'Failed to delete business logo')
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  // Missing ManageBookings API functions
  async getAllEmployeesCalendar(token, fromDate, toDate) {
    try {
      const response = await fetch(buildUrl('managebookings', '/getallemployeescalendar'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          fromDate: fromDate,
          toDate: toDate
        })
      });

      const data = await response.json();
      return {
        success: data.success,
        data: data.success ? (data.data?.bookings || data.calendar || data.bookings) : null,
        summary: data.success ? (data.data?.summary || data.summary) : null,
        error: data.success ? null : (data.error || 'Failed to fetch all employees calendar')
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  async processBookingPayment(token, bookingId, paymentAmount, paymentMethod) {
    try {
      const response = await fetch(buildUrl('managebookings', '/processbookingpayment'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          bookingId: bookingId,
          paymentAmount: paymentAmount,
          paymentMethod: paymentMethod
        })
      });

      const data = await response.json();
      return {
        success: data.success,
        data: data.success ? (data.data || data.payment || data) : null,
        error: data.success ? null : (data.error || 'Failed to process booking payment')
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  async getBookingAnalytics(token, fromDate, toDate, groupBy) {
    try {
      const response = await fetch(buildUrl('managebookings', '/getbookinganalytics'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          fromDate: fromDate,
          toDate: toDate,
          groupBy: groupBy
        })
      });

      const data = await response.json();

      // Backend returns analytics data at root level, not nested
      const { success, error, ...analyticsData } = data;
      const result = {
        success: success,
        data: success ? analyticsData : null,
        error: success ? null : (error || 'Failed to fetch booking analytics')
      };
      return result;
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  async autoCalculateMetrics(token, bookingId) {
    try {
      const response = await fetch(buildUrl('managebookings', '/autocalculatemetrics'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          bookingId: bookingId
        })
      });

      const data = await response.json();
      return {
        success: data.success,
        data: data.success ? (data.data || data.metrics) : null,
        error: data.success ? null : (data.error || 'Failed to auto-calculate metrics')
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  async getBookingsByStatus(token, status, fromDate, toDate) {
    try {
      const response = await fetch(buildUrl('managebookings', '/getbookingsbystatus'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          status: status,
          fromDate: fromDate,
          toDate: toDate
        })
      });

      const data = await response.json();
      return {
        success: data.success,
        data: data.success ? (data.data?.bookings || data.bookings) : null,
        error: data.success ? null : (data.error || 'Failed to fetch bookings by status')
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  async bulkUpdateBookingStatus(token, bookingIds, newStatus) {
    try {
      const response = await fetch(buildUrl('managebookings', '/bulkupdatebookingstatus'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          bookingIds: bookingIds,
          newStatus: newStatus
        })
      });

      const data = await response.json();
      return {
        success: data.success,
        data: data.success ? (data.data?.results || data.results) : null,
        error: data.success ? null : (data.error || 'Failed to bulk update booking status')
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },
  // Store tokens in localStorage or sessionStorage based on keepSignedIn
  storeTokens(token, refreshKey, keepSignedIn = true) {
    const storage = keepSignedIn ? localStorage : sessionStorage;
    storage.setItem('sessionToken', token);
    storage.setItem('refreshKey', refreshKey);

    // Clear from the other storage to avoid conflicts
    const otherStorage = keepSignedIn ? sessionStorage : localStorage;
    otherStorage.removeItem('sessionToken');
    otherStorage.removeItem('refreshKey');
  },

  // Get stored tokens from both localStorage and sessionStorage
  getTokens() {
    // Check localStorage first, then sessionStorage
    let token = localStorage.getItem('sessionToken');
    let refreshKey = localStorage.getItem('refreshKey');

    if (!token) {
      token = sessionStorage.getItem('sessionToken');
      refreshKey = sessionStorage.getItem('refreshKey');
    }

    return {
      token,
      refreshKey
    };
  },

  // Clear stored tokens from both storages
  clearTokens() {
    localStorage.removeItem('sessionToken');
    localStorage.removeItem('refreshKey');
    sessionStorage.removeItem('sessionToken');
    sessionStorage.removeItem('refreshKey');
  },

  // Check if user is authenticated
  isAuthenticated() {
    const { token } = this.getTokens();
    return !!token;
  },

  // Decode token to get user info (simple base64 decode - not for security validation!)
  decodeToken(token) {
    try {
      const payload = token.split('.')[1];
      const decoded = JSON.parse(atob(payload));
      return {
        success: true,
        data: {
          userId: decoded.userId || decoded.sub,
          employeeId: decoded.employeeId,
          role: decoded.role,
          businessId: decoded.businessId || decoded.tennantId || decoded.tenantId,
          tokenType: decoded.tokenType,
          iat: decoded.iat,
          exp: decoded.exp
        }
      };
    } catch (error) {
      return {
        success: false,
        error: 'Invalid token format'
      };
    }
  },

  // ==================== MANAGE SERVICES API ====================

  async getServices(token, isActive = null) {
    let lastError;
    for (let attempt = 0; attempt < 2; attempt++) {
      try {
        const timeout = attempt === 0 ? 8000 : 30000;

        const response = await fetchWithTimeout(buildUrl('manageservices', '/getservices'), {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({
            isActive
          })
        }, timeout);

        const data = await response.json();

        return {
          success: data.success,
          data: data.success ? (data.services || data.data || []) : null,
          error: data.success ? null : (data.error || 'Failed to fetch services')
        };
      } catch (error) {
        lastError = error;
        if (attempt === 0 && error.message.includes('timeout')) {
          continue;
        }
        break;
      }
    }

    return {
      success: false,
      error: lastError?.message || 'Network error occurred'
    };
  },

  async getService(token, serviceId) {
    try {
      const response = await fetch(buildUrl('manageservices', '/getservice'), {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          serviceId
        })
      });

      const data = await response.json();
      const { success, error, ...serviceData } = data;
      return {
        success,
        data: success ? serviceData : null,
        error: success ? null : (error || 'Failed to fetch service')
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  async createService(token, serviceName, description = null, duration, price) {
    try {
      const response = await fetch(buildUrl('manageservices', '/createservice'), {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          serviceName,
          description,
          duration,
          price
        })
      });

      const data = await response.json();

      return {
        success: data.success,
        data: data.success ? data.data : null,
        error: data.success ? null : (data.error || 'Failed to create service')
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  async updateService(token, serviceId, serviceName = null, description = null, duration = null, price = null) {
    try {
      const response = await fetch(buildUrl('manageservices', '/updateservice'), {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          serviceId,
          serviceName,
          description,
          duration,
          price
        })
      });

      const data = await response.json();
      return {
        success: data.success,
        data: data.success ? data.data : null,
        error: data.success ? null : (data.error || 'Failed to update service')
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  async deactivateService(token, serviceId) {
    try {
      const response = await fetch(buildUrl('manageservices', '/deactivateservice'), {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          serviceId
        })
      });

      const data = await response.json();

      return {
        success: data.success,
        data: data.success ? data.data : null,
        error: data.success ? null : (data.error || 'Failed to deactivate service')
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  async reactivateService(token, serviceId) {
    try {
      const response = await fetch(buildUrl('manageservices', '/reactivateservice'), {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          serviceId
        })
      });

      const data = await response.json();

      return {
        success: data.success,
        data: data.success ? data.data : null,
        error: data.success ? null : (data.error || 'Failed to reactivate service')
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  async deleteService(token, serviceId) {
    try {
      const response = await fetch(buildUrl('manageservices', '/deleteservice'), {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          serviceId
        })
      });

      const data = await response.json();

      return {
        success: data.success,
        data: data.success ? data.data : null,
        error: data.success ? null : (data.error || 'Failed to delete service'),
        errorData: data.success ? null : data.data // Include additional error data (e.g., totalBookings)
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  async exportServices(token, format = 'csv', includeAnalytics = false) {
    try {
      const response = await fetch(buildUrl('manageservices', '/exportservices'), {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          format,
          includeAnalytics
        })
      });

      const data = await response.json();

      // Handle different possible response structures
      const exportData = data.success ? (data.exportData || data.data || data.content) : null;

      return {
        success: data.success,
        data: exportData,
        error: data.success ? null : (data.error || 'Failed to export services')
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  async getServiceAnalytics(token, serviceId = null, fromDate = null, toDate = null) {
    try {
      const response = await fetch(buildUrl('manageservices', '/getserviceanalytics'), {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          serviceId,
          fromDate,
          toDate
        })
      });

      const data = await response.json();
      return {
        success: data.success,
        data: data.success ? (data.analytics || data.data) : null,
        summary: data.success ? (data.summary || data.analytics?.summary) : null,
        error: data.success ? null : (data.error || 'Failed to fetch service analytics')
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  async getPopularServices(token, fromDate = null, toDate = null, limit = 10) {
    try {
      const response = await fetch(buildUrl('manageservices', '/getpopularservices'), {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fromDate,
          toDate,
          limit
        })
      });

      const data = await response.json();
      return {
        success: data.success,
        data: data.success ? (data.services || data.data) : null,
        period: data.success ? data.period : null,
        error: data.success ? null : (data.error || 'Failed to fetch popular services')
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  async getServiceRevenue(token, fromDate = null, toDate = null, groupBy = 'day') {
    try {
      const response = await fetch(buildUrl('manageservices', '/getservicerevenue'), {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fromDate,
          toDate,
          groupBy
        })
      });

      const data = await response.json();
      return {
        success: data.success,
        data: data.success ? (data.revenue || data.data) : null,
        period: data.success ? data.period : null,
        error: data.success ? null : (data.error || 'Failed to fetch service revenue')
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  // ============================================
  // LEAVE MANAGEMENT FUNCTIONS
  // ============================================

  async viewLeaveRequests(token, status = null, employeeId = null, fromDate = null, toDate = null) {
    try {
      // Only include non-null parameters in the request body
      const body = {};
      if (status !== null && status !== undefined) body.status = status;
      if (employeeId !== null && employeeId !== undefined) body.employeeId = employeeId;
      if (fromDate !== null && fromDate !== undefined) body.fromDate = fromDate;
      if (toDate !== null && toDate !== undefined) body.toDate = toDate;

      const response = await fetchWithTimeout(buildUrl('manageleave', '/viewleaverequests'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });

      const data = await response.json();

      return {
        success: data.success,
        data: data.success ? data.data : null,
        summary: data.success ? data.summary : null,
        error: data.success ? null : (data.error || 'Failed to fetch leave requests')
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  async reviewLeaveRequest(token, leaveRequestId, status, reviewComments = null, allowNegativeBalance = false, overrideReason = null) {
    try {
      const response = await fetchWithTimeout(buildUrl('manageleave', '/reviewleaverequest'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          leaveRequestId,
          status,
          reviewComments,
          allowNegativeBalance,
          overrideReason
        })
      });

      const data = await response.json();

      return {
        success: data.success,
        message: data.success ? data.message : null,
        // Preserve full error object structure for code-based error handling
        error: data.success ? null : (data.error || 'Failed to review leave request')
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  async getLeaveBalances(token, userId = null) {
    try {
      const response = await fetchWithTimeout(buildUrl('manageleave', '/getleavebalances'), {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId
        })
      });

      const data = await response.json();

      return {
        success: data.success,
        data: data.success ? data.data : null,
        error: data.success ? null : (data.error || 'Failed to fetch leave balances')
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  async getLeavePolicyGroups(token) {
    try {
      const response = await fetchWithTimeout(buildUrl('manageleave', '/getleavepolicygroups'), {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionToken: token
        })
      });

      const data = await response.json();
      return {
        success: data.success,
        data: data.success ? data.data : null,
        error: data.success ? null : (data.error || 'Failed to fetch leave policy groups')
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  async createLeavePolicyGroup(token, groupName, groupDescription = null) {
    try {
      const response = await fetchWithTimeout(buildUrl('manageleave', '/createleavepolicygroup'), {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          groupName,
          groupDescription
        })
      });

      const data = await response.json();
      return {
        success: data.success,
        data: data.success ? data.data : null,
        error: data.success ? null : (data.error || 'Failed to create leave policy group')
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  async editLeavePolicyGroup(token, policyGroupId, groupName, _groupDescription = null) {
    try {
      const response = await fetchWithTimeout(buildUrl('manageleave', '/editleavepolicygroup'), {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          leavePolicyGroupId: policyGroupId,
          groupName
        })
      });

      const data = await response.json();
      return {
        success: data.success,
        message: data.success ? data.message : null,
        error: data.success ? null : (data.error || 'Failed to edit leave policy group')
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  async deleteLeavePolicyGroup(token, policyGroupId) {
    try {
      const response = await fetchWithTimeout(buildUrl('manageleave', '/deleteleavepolicygroup'), {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          leavePolicyGroupId: policyGroupId
        })
      });

      const data = await response.json();
      return {
        success: data.success,
        message: data.success ? data.message : null,
        error: data.success ? null : (data.error || 'Failed to delete leave policy group')
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  async getLeavePolicies(token, policyGroupId = null) {
    try {
      const response = await fetchWithTimeout(buildUrl('manageleave', '/getleavepolicies'), {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          leavePolicyGroupId: policyGroupId
        })
      });

      const data = await response.json();
      return {
        success: data.success,
        data: data.success ? data.data : null,
        error: data.success ? null : (data.error || 'Failed to fetch leave policies')
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  async createLeavePolicy(token, policyGroupId, leaveTypeId, entitlement, accrualPeriod, maxBalance, isPaid) {
    try {
      const response = await fetchWithTimeout(buildUrl('manageleave', '/createleavepolicy'), {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          leavePolicyGroupId: policyGroupId,
          leaveTypeId,
          entitlement,
          accrualPeriodMonths: accrualPeriod,
          maximumBalance: maxBalance,
          isPaid
        })
      });

      const data = await response.json();
      return {
        success: data.success,
        data: data.success ? data.data : null,
        error: data.success ? null : (data.error || 'Failed to create leave policy')
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  async editLeavePolicy(token, leavePolicyId, entitlement, accrualPeriod, maxBalance, isPaid) {
    try {
      const response = await fetchWithTimeout(buildUrl('manageleave', '/editleavepolicy'), {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          leavePolicyId,
          entitlement,
          accrualPeriodMonths: accrualPeriod,
          maximumBalance: maxBalance,
          isPaid
        })
      });

      const data = await response.json();
      return {
        success: data.success,
        message: data.success ? data.message : null,
        error: data.success ? null : (data.error || 'Failed to edit leave policy')
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  async deleteLeavePolicy(token, leavePolicyId) {
    try {
      const response = await fetchWithTimeout(buildUrl('manageleave', '/deleteleavepolicy'), {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          leavePolicyId
        })
      });

      const data = await response.json();
      return {
        success: data.success,
        message: data.success ? data.message : null,
        error: data.success ? null : (data.error || 'Failed to delete leave policy')
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  async getLeaveTypes(token) {
    try {
      const response = await fetchWithTimeout(buildUrl('manageleave', '/getleavetypes'), {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionToken: token
        })
      });

      const data = await response.json();

      return {
        success: data.success,
        data: data.success ? data.data : null,
        error: data.success ? null : (data.error || 'Failed to fetch leave types')
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  async getMinimumRequirements(token) {
    try {
      const response = await fetchWithTimeout(buildUrl('manageleave', '/getminimumrequirements'), {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionToken: token
        })
      });

      const data = await response.json();
      return {
        success: data.success,
        data: data.success ? data.data : null,
        error: data.success ? null : (data.error || 'Failed to fetch minimum requirements')
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  async assignEmployeeToPolicyGroup(token, employeeId, leavePolicyGroupId) {
    try {
      const response = await fetchWithTimeout(buildUrl('manageleave', '/assignemployeetopolicygroup'), {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          employeeId,
          leavePolicyGroupId
        })
      });

      const data = await response.json();

      // CACHE INVALIDATION: Clear employees cache on successful assignment
      if (data.success) {
        const businessId = getBusinessIdFromToken(token);
        if (businessId) {
          cache.invalidate('employees', businessId);
        }
      }

      return {
        success: data.success,
        message: data.success ? data.message : null,
        error: data.success ? null : (data.error || 'Failed to assign employee to policy group')
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  async createLeaveRequest(token, leaveTypeId, startDate, endDate, notes = null) {
    try {
      const response = await fetchWithTimeout(buildUrl('manageleave', '/createleaverequest'), {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          leaveTypeId,
          startDate,
          endDate,
          notes
        })
      });

      const data = await response.json();

      return {
        success: data.success,
        data: data.success ? data.data : null,
        message: data.success ? data.message : null,
        error: data.success ? null : (data.error || 'Failed to create leave request')
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  async getLeaveCalendar(token, fromDate = null, toDate = null) {
    try {
      const response = await fetchWithTimeout(buildUrl('manageleave', '/getleavecalendar'), {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fromDate,
          toDate
        })
      });

      const data = await response.json();

      return {
        success: data.success,
        calendar: data.success ? data.calendar : {},
        summary: data.success ? data.summary : null,
        error: data.success ? null : (data.error || 'Failed to fetch leave calendar')
      };
    } catch (error) {
      return {
        success: false,
        calendar: {},
        error: error.message || 'Network error occurred'
      };
    }
  },

  async getLeaveAnalytics(token, fromDate = null, toDate = null) {
    try {
      const requestBody = {
        sessionToken: token
      };

      if (fromDate) requestBody.fromDate = fromDate;
      if (toDate) requestBody.toDate = toDate;

      const response = await fetchWithTimeout(buildUrl('manageleave', '/getleaveanalytics'), {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody)
      });

      const data = await response.json();

      return {
        success: data.success,
        analytics: data.success ? data.analytics : null,
        error: data.success ? null : (data.error || 'Failed to fetch leave analytics')
      };
    } catch (error) {
      return {
        success: false,
        analytics: null,
        error: error.message || 'Network error occurred'
      };
    }
  },

  // ===============================================
  // STOCK MANAGEMENT API METHODS
  // ===============================================

  // Get all stock items with filters
  async getStockItems(token, filters = {}) {
    try {
      const { isActive, searchTerm, category, pageNumber, pageSize } = filters;
      const url = buildUrl('managestock', '/getstockitems');
      const requestBody = {
        sessionToken: token,
        isActive,
        searchTerm,
        category,
        pageNumber: pageNumber || 1,
        pageSize: pageSize || 50
      };

      const response = await fetchWithTimeout(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody)
      });

      const data = await response.json();

      return {
        success: data.success,
        data: data.success ? data.data : [],
        count: data.success ? (data.pagination?.totalCount || data.count || data.data?.length || 0) : 0,
        pagination: data.pagination,
        error: data.success ? null : (data.error || 'Failed to fetch stock items')
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  // Get single stock item by ID
  async getStockItem(token, stockItemId) {
    try {
      const response = await fetchWithTimeout(buildUrl('managestock', '/getstockitem'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionToken: token,
          stockItemId
        })
      });

      const data = await response.json();
      return {
        success: data.success,
        data: data.success ? data.data : null,
        error: data.success ? null : (data.error || 'Failed to fetch stock item')
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  // Create new stock item
  async createStockItem(token, itemData) {
    try {
      const response = await fetchWithTimeout(buildUrl('managestock', '/createstockitem'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionToken: token,
          ...itemData
        })
      });

      const data = await response.json();
      return {
        success: data.success,
        data: data.success ? data.data : null,
        message: data.success ? data.message : null,
        error: data.success ? null : (data.error || 'Failed to create stock item')
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  // Update stock item details
  async updateStockItem(token, stockItemId, itemData) {
    try {
      const response = await fetchWithTimeout(buildUrl('managestock', '/updatestockitem'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionToken: token,
          stockItemId,
          ...itemData
        })
      });

      const data = await response.json();
      return {
        success: data.success,
        data: data.success ? data.data : null,
        message: data.success ? data.message : null,
        error: data.success ? null : (data.error || 'Failed to update stock item')
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  // Deactivate stock item
  async deactivateStockItem(token, stockItemId) {
    try {
      const response = await fetchWithTimeout(buildUrl('managestock', '/deactivatestockitem'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionToken: token,
          stockItemId
        })
      });

      const data = await response.json();
      return {
        success: data.success,
        message: data.success ? data.message : null,
        error: data.success ? null : (data.error || 'Failed to deactivate stock item')
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  // Purchase stock (add inventory)
  async purchaseStock(token, stockItemId, quantity, unitPrice, supplier, notes) {
    try {
      const requestBody = {
        sessionToken: token,
        stockItemId,
        quantity,
        unitPrice,
        supplier,
        notes
      };

      const response = await fetchWithTimeout(buildUrl('managestock', '/purchasestock'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody)
      });

      const data = await response.json();

      return {
        success: data.success,
        data: data.success ? data.data : null,
        message: data.success ? data.message : null,
        error: data.success ? null : (data.error || 'Failed to record purchase')
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  // Sell stock (reduce inventory)
  async sellStock(token, stockItemId, quantity, unitPrice, notes, customerName = null, clientId = null, bookingId = null, reference = null) {
    try {
      const requestBody = {
        sessionToken: token,
        stockItemId,
        quantity,
        unitPrice,
        notes,
        ...(customerName && { customerName }),
        ...(clientId && { clientId }),
        ...(bookingId && { bookingId }),
        ...(reference && { reference })
      };

      const response = await fetchWithTimeout(buildUrl('managestock', '/sellstock'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody)
      });

      const data = await response.json();

      return {
        success: data.success,
        data: data.success ? data.data : null,
        message: data.success ? data.message : null,
        error: data.success ? null : (data.error || 'Failed to record sale')
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  // ===== VOUCHER MANAGEMENT =====

  async getVoucherTypes(token, filters = {}) {
    try {
      const response = await fetchWithTimeout(buildUrl('managestock', '/getvouchertypes'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionToken: token, ...filters })
      });
      const data = await response.json();
      return { success: data.success, data: data.success ? data.data : null, error: data.success ? null : (data.error || 'Failed to load voucher types') };
    } catch (error) {
      return { success: false, error: error.message || 'Network error occurred' };
    }
  },

  async createVoucherType(token, voucherTypeData) {
    try {
      const response = await fetchWithTimeout(buildUrl('managestock', '/createvouchertype'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionToken: token, ...voucherTypeData })
      });
      const data = await response.json();
      return { success: data.success, data: data.success ? data.data : null, error: data.success ? null : (data.error || 'Failed to create voucher type') };
    } catch (error) {
      return { success: false, error: error.message || 'Network error occurred' };
    }
  },

  async updateVoucherType(token, voucherTypeId, updates) {
    try {
      const response = await fetchWithTimeout(buildUrl('managestock', '/updatevouchertype'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionToken: token, voucherTypeId, ...updates })
      });
      const data = await response.json();
      return { success: data.success, data: data.success ? data.data : null, error: data.success ? null : (data.error || 'Failed to update voucher type') };
    } catch (error) {
      return { success: false, error: error.message || 'Network error occurred' };
    }
  },

  async deactivateVoucherType(token, voucherTypeId) {
    try {
      const response = await fetchWithTimeout(buildUrl('managestock', '/deactivatevouchertype'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionToken: token, voucherTypeId })
      });
      const data = await response.json();
      return { success: data.success, data: data.success ? data.data : null, error: data.success ? null : (data.error || 'Failed to deactivate voucher type') };
    } catch (error) {
      return { success: false, error: error.message || 'Network error occurred' };
    }
  },

  async purchaseVoucher(token, voucherTypeId, clientId = null, quantity = 1, customAmount) {
    try {
      const response = await fetchWithTimeout(buildUrl('managestock', '/purchasevoucher'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionToken: token, voucherTypeId, ...(clientId && { clientId }), quantity, ...(customAmount !== undefined && { customAmount }) })
      });
      const data = await response.json();
      return { success: data.success, data: data.success ? data.data : null, error: data.success ? null : (data.error || 'Failed to purchase voucher') };
    } catch (error) {
      return { success: false, error: error.message || 'Network error occurred' };
    }
  },

  async validateVoucher(token, voucherCode, cartTotal = 0) {
    try {
      const response = await fetchWithTimeout(buildUrl('managestock', '/validatevoucher'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionToken: token, voucherCode, cartTotal })
      });
      const data = await response.json();
      return { success: data.success, data: data.success ? data.data : null, error: data.success ? null : (data.error || 'Invalid voucher code') };
    } catch (error) {
      return { success: false, error: error.message || 'Network error occurred' };
    }
  },

  async redeemVoucher(token, voucherCode, saleReference = null) {
    try {
      const response = await fetchWithTimeout(buildUrl('managestock', '/redeemvoucher'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionToken: token, voucherCode, ...(saleReference && { saleReference }) })
      });
      const data = await response.json();
      return { success: data.success, data: data.success ? data.data : null, error: data.success ? null : (data.error || 'Failed to redeem voucher') };
    } catch (error) {
      return { success: false, error: error.message || 'Network error occurred' };
    }
  },

  async getVoucherInstances(token, filters = {}) {
    try {
      const response = await fetchWithTimeout(buildUrl('managestock', '/getvoucherinstances'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionToken: token, ...filters })
      });
      const data = await response.json();
      return { success: data.success, data: data.success ? data.data : null, error: data.success ? null : (data.error || 'Failed to load voucher instances') };
    } catch (error) {
      return { success: false, error: error.message || 'Network error occurred' };
    }
  },

  // Adjust stock (damaged, stolen, found, correction)
  async adjustStock(token, stockItemId, adjustment, reason, adjustmentReasonType, notes) {
    try {
      const requestBody = {
        sessionToken: token,
        stockItemId,
        adjustmentQuantity: adjustment, // Backend expects adjustmentQuantity
        reason,
        adjustmentType: adjustmentReasonType, // Backend expects adjustmentType
        notes
      };

      const response = await fetchWithTimeout(buildUrl('managestock', '/adjuststock'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody)
      });

      const data = await response.json();

      return {
        success: data.success,
        data: data.success ? data.data : null,
        message: data.success ? data.message : null,
        error: data.success ? null : (data.error || 'Failed to adjust stock')
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  // Get stock transactions with filters
  async getStockTransactions(token, stockItemId, filters = {}) {
    try {
      const response = await fetchWithTimeout(buildUrl('managestock', '/getstocktransactions'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionToken: token,
          stockItemId,
          ...filters
        })
      });

      const data = await response.json();
      return {
        success: data.success,
        data: data.success ? data.data : [],
        error: data.success ? null : (data.error || 'Failed to fetch transactions')
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  // Get current stock levels
  async getStockLevels(token, filters = {}) {
    try {
      const response = await fetchWithTimeout(buildUrl('managestock', '/getstocklevels'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionToken: token,
          ...filters
        })
      });

      const data = await response.json();
      return {
        success: data.success,
        data: data.success ? data.data : [],
        error: data.success ? null : (data.error || 'Failed to fetch stock levels')
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  // Get low stock alerts
  async getLowStockAlerts(token) {
    try {
      const response = await fetchWithTimeout(buildUrl('managestock', '/getlowstockalerts'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionToken: token
        })
      });

      const data = await response.json();
      return {
        success: data.success,
        data: data.success ? data.data : [],
        count: data.success ? data.count : 0,
        error: data.success ? null : (data.error || 'Failed to fetch low stock alerts')
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  // Get stock analytics
  async getStockAnalytics(token, startDate, endDate) {
    try {
      const requestBody = { sessionToken: token };
      if (startDate) requestBody.startDate = startDate;
      if (endDate) requestBody.endDate = endDate;

      const response = await fetchWithTimeout(buildUrl('managestock', '/getstockanalytics'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody)
      });

      const data = await response.json();
      return {
        success: data.success,
        data: data.success ? data.data : null,
        error: data.success ? null : (data.error || 'Failed to fetch analytics')
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  // Get unlinked retail sales (walk-in/manager sales not tied to bookings)
  async getUnlinkedRetailSales(token, fromDate, toDate) {
    try {
      const response = await fetchWithTimeout(buildUrl('managestock', '/getunlinkedretailsales'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          sessionToken: token,
          fromDate,
          toDate
        })
      });

      const data = await response.json();
      return {
        success: data.success,
        data: data.success ? {
          totalAmount: data.totalAmount || 0,
          totalQuantity: data.totalQuantity || 0,
          transactionCount: data.transactionCount || 0,
          fromDate: data.fromDate,
          toDate: data.toDate
        } : null,
        error: data.success ? null : (data.error || 'Failed to fetch unlinked retail sales')
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  // Export stock data
  async exportStockData(token, format = 'csv') {
    try {
      const response = await fetchWithTimeout(buildUrl('managestock', '/exportstockdata'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionToken: token,
          format
        })
      });

      const data = await response.json();
      return {
        success: data.success,
        data: data.success ? data.data : null,
        error: data.success ? null : (data.error || 'Failed to export data')
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  // ============================================
  // PAYSTACK PAYMENT METHODS (Public - no auth)
  // ============================================

  /**
   * Initialize a Paystack payment for live event tickets
   * @param {Object} paymentData - Payment details
   * @param {string} paymentData.email - Customer email
   * @param {number} paymentData.amount - Amount in ZAR (e.g., 500 for R500)
   * @param {string} paymentData.customerName - Customer full name
   * @param {string} paymentData.customerPhone - Customer phone number
   * @param {number} paymentData.ticketCount - Number of tickets
   * @param {string} [paymentData.eventId] - Optional event ID
   * @param {string} [paymentData.eventName] - Optional event name
   * @returns {Promise<{success: boolean, data?: {reference: string, authorizationUrl: string, accessCode: string}, error?: string}>}
   */
  async initializePaystackPayment(paymentData) {
    try {
      const response = await fetchWithTimeout(buildUrl('managepayments', '/initializepaystackpayment'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(paymentData)
      });

      const data = await response.json();
      return {
        success: data.success,
        data: data.success ? {
          reference: data.reference,
          authorizationUrl: data.authorizationUrl,
          accessCode: data.accessCode,
          message: data.message
        } : null,
        error: data.success ? null : (data.error || 'Failed to initialize payment')
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  /**
   * Verify a Paystack payment by reference
   * @param {string} reference - Payment reference (e.g., HRMA-1234567890-ABCD1234)
   * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
   */
  async verifyPaystackPayment(reference) {
    try {
      const response = await fetchWithTimeout(buildUrl('managepayments', '/verifypaystackpayment'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reference })
      });

      const data = await response.json();
      return {
        success: data.success,
        data: data.success ? data : null,
        error: data.success ? null : (data.error || 'Failed to verify payment')
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  // ===== Billing / Subscription API methods =====

  async getActiveSubscription(sessionToken) {
    try {
      const response = await fetchWithTimeout(buildUrl('managepayments', '/getactivesubscription'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionToken })
      });
      const { success, error: apiError, ...rest } = await response.json();
      return { success, data: success ? rest : null, error: apiError || null };
    } catch (error) {
      return { success: false, error: error.message || 'Network error' };
    }
  },

  async getPaymentHistory(sessionToken) {
    try {
      const response = await fetchWithTimeout(buildUrl('managepayments', '/getpaymenthistory'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionToken })
      });
      const { success, error: apiError, ...rest } = await response.json();
      return { success, data: success ? rest : null, error: apiError || null };
    } catch (error) {
      return { success: false, error: error.message || 'Network error' };
    }
  },

  async getPaymentMethods(sessionToken) {
    try {
      const response = await fetchWithTimeout(buildUrl('managepayments', '/getpaymentmethods'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionToken })
      });
      const { success, error: apiError, ...rest } = await response.json();
      return { success, data: success ? rest : null, error: apiError || null };
    } catch (error) {
      return { success: false, error: error.message || 'Network error' };
    }
  },

  async initializeSubscriptionPayment(sessionToken, planData) {
    try {
      const response = await fetchWithTimeout(buildUrl('managepayments', '/initializesubscriptionpayment'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionToken, ...planData })
      });
      const { success, error: apiError, ...rest } = await response.json();
      return { success, data: success ? rest : null, error: apiError || null };
    } catch (error) {
      return { success: false, error: error.message || 'Network error' };
    }
  },

  async getInvoices(sessionToken) {
    try {
      const response = await fetchWithTimeout(buildUrl('managepayments', '/getinvoices'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionToken })
      });
      const { success, error: apiError, ...rest } = await response.json();
      return { success, data: success ? rest : null, error: apiError || null };
    } catch (error) {
      return { success: false, error: error.message || 'Network error' };
    }
  },

  async verifySubscriptionPayment(sessionToken, reference) {
    try {
      const response = await fetchWithTimeout(buildUrl('managepayments', '/verifysubscriptionpayment'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionToken, reference })
      });
      const { success, error: apiError, ...rest } = await response.json();
      return { success, data: success ? rest : null, error: apiError || null };
    } catch (error) {
      return { success: false, error: error.message || 'Network error' };
    }
  },

  // ===== Public Plans (no auth) =====

  async getVisiblePlans() {
    try {
      const response = await fetchWithTimeout(buildUrl('managepayments', '/getvisibleplans?platform=hrma'));
      const { success, error: apiError, ...rest } = await response.json();
      return { success, data: success ? rest : null, error: apiError || null };
    } catch (error) {
      return { success: false, error: error.message || 'Network error' };
    }
  },

  // ===== Support System =====

  async createChatSession(token, data) {
    const response = await fetchWithTimeout(buildUrl('support', '/createchatsession'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sessionToken: token, ...data })
    });
    return response.json();
  },

  async getConversations(token, status) {
    const response = await fetchWithTimeout(buildUrl('support', '/getconversations'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sessionToken: token, status })
    });
    return response.json();
  },

  async getMessages(token, conversationId, page, pageSize) {
    const response = await fetchWithTimeout(buildUrl('support', '/getmessages'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sessionToken: token, conversationId, page, pageSize })
    });
    return response.json();
  },

  async sendMessage(token, conversationId, message) {
    const response = await fetchWithTimeout(buildUrl('support', '/sendmessage'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sessionToken: token, conversationId, message })
    });
    return response.json();
  },

  async pollMessages(token, conversationId, since) {
    const response = await fetchWithTimeout(buildUrl('support', '/pollmessages'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sessionToken: token, conversationId, since })
    });
    return response.json();
  },

  async submitBugReport(token, data) {
    const response = await fetchWithTimeout(buildUrl('support', '/submitbugreport'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sessionToken: token, ...data })
    });
    return response.json();
  },

  async getNotificationPreferences(token) {
    const response = await fetchWithTimeout(buildUrl('support', '/getnotificationpreferences'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sessionToken: token })
    });
    return response.json();
  },

  async updateNotificationPreferences(token, data) {
    const response = await fetchWithTimeout(buildUrl('support', '/updatenotificationpreferences'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sessionToken: token, ...data })
    });
    return response.json();
  },

  async getUnreadCount(token) {
    const response = await fetchWithTimeout(buildUrl('support', '/getunreadcount'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sessionToken: token })
    });
    return response.json();
  }
};

// Mutex for token refresh - ensures only one refresh happens at a time
let refreshPromise = null;

// Helper function to handle API calls with automatic token refresh
const makeAuthenticatedRequest = async (requestFn) => {
  const { token, refreshKey } = tokenUtils.getTokens();

  if (!token) {
    return {
      success: false,
      error: 'No authentication token found. Please log in again.'
    };
  }

  // First attempt with current token
  let result = await requestFn(token);

  // If token expired/invalid and we have a refresh key, try to refresh
  const errorMessage = typeof result.error === 'string' ? result.error.toLowerCase() : '';
  const shouldRefresh = !result.success &&
      (errorMessage.includes('expired') ||
       errorMessage.includes('session token expired') ||
       errorMessage.includes('invalid') ||
       errorMessage.includes('invalid session token') ||
       errorMessage.includes('session token required'));

  if (shouldRefresh && refreshKey) {
    // Use mutex to prevent concurrent refresh attempts
    if (!refreshPromise) {
      refreshPromise = (async () => {
        try {
          return await apiService.refreshSessionToken(refreshKey);
        } finally {
          refreshPromise = null;
        }
      })();
    }
    const refreshResult = await refreshPromise;

    if (refreshResult.success) {
      // Store new tokens (preserve keepSignedIn preference)
      const keepSignedIn = localStorage.getItem('sessionToken') !== null;
      const newToken = refreshResult.data.sessionToken || refreshResult.data.token;
      const newRefreshKey = refreshResult.data.refreshKey;

      tokenUtils.storeTokens(newToken, newRefreshKey, keepSignedIn);

      // Retry the original request with new token
      result = await requestFn(newToken);
    } else {
      // Refresh failed, user needs to login again
      tokenUtils.clearTokens();
      return {
        success: false,
        error: 'Session expired. Please log in again.'
      };
    }
  }

  return result;
};

// Enhanced API service with automatic token refresh
export const apiServiceWithRefresh = {
  async getHRPolicies() {
    return makeAuthenticatedRequest(async (token) => {
      return apiService.getHRPolicies(token);
    });
  },

  async upsertHRPolicy(policyName, policyFile, category) {
    return makeAuthenticatedRequest(async (token) => {
      return apiService.upsertHRPolicy(token, policyName, policyFile, category);
    });
  },

  async deleteHRPolicy(policyName) {
    return makeAuthenticatedRequest(async (token) => {
      return apiService.deleteHRPolicy(token, policyName);
    });
  },

  async viewHRPolicy(policyName) {
    return makeAuthenticatedRequest(async (token) => {
      return apiService.viewHRPolicy(token, policyName);
    });
  },

  async clockIn() {
    return makeAuthenticatedRequest(async (token) => {
      return apiService.clockIn(token);
    });
  },

  async clockOut(isTraining = false) {
    return makeAuthenticatedRequest(async (token) => {
      return apiService.clockOut(token, isTraining);
    });
  },

  async getClockStatus() {
    return makeAuthenticatedRequest(async (token) => {
      return apiService.getClockStatus(token);
    });
  },

  async getClockStatusMultiple() {
    return makeAuthenticatedRequest(async (token) => {
      return apiService.getClockStatusMultiple(token);
    });
  },

  async getMVV() {
    return makeAuthenticatedRequest(async (token) => {
      return apiService.getMVV(token);
    });
  },

  async editMVV(mission, vision, values) {
    return makeAuthenticatedRequest(async (token) => {
      return apiService.editMVV(token, mission, vision, values);
    });
  },

  // Goal Tracking methods with automatic token refresh
  async createGoalEntry(entryData) {
    return makeAuthenticatedRequest(async (token) => {
      return apiService.createGoalEntry(token, entryData);
    });
  },

  async getSingleGoalEntry(goalTrackingEntryId) {
    return makeAuthenticatedRequest(async (token) => {
      return apiService.getSingleGoalEntry(token, goalTrackingEntryId);
    });
  },

  async getGoalEntries(userId, fromDate, toDate) {
    return makeAuthenticatedRequest(async (token) => {
      return apiService.getGoalEntries(token, userId, fromDate, toDate);
    });
  },

  async getBusinessGoalSummary(fromDate, toDate) {
    return makeAuthenticatedRequest(async (token) => {
      return apiService.getBusinessGoalSummary(token, fromDate, toDate);
    });
  },

  async editGoalEntry(goalTrackingEntryId, updates) {
    return makeAuthenticatedRequest(async (token) => {
      return apiService.editGoalEntry(token, goalTrackingEntryId, updates);
    });
  },

  async autoPopulateFromBookings(date, userId) {
    return makeAuthenticatedRequest(async (token) => {
      return apiService.autoPopulateFromBookings(token, date, userId);
    });
  },

  async getCurrentIncentive(userId) {
    return makeAuthenticatedRequest(async (token) => {
      return apiService.getCurrentIncentive(token, userId);
    });
  },

  async getBonusLevel(userId) {
    return makeAuthenticatedRequest(async (token) => {
      return apiService.getBonusLevel(token, userId);
    });
  },

  async getQuarterlyBonus(userId) {
    return makeAuthenticatedRequest(async (token) => {
      return apiService.getQuarterlyBonus(token, userId);
    });
  },

  async getHoursSummary(userId, fromDate, toDate) {
    return makeAuthenticatedRequest(async (token) => {
      return apiService.getHoursSummary(token, userId, fromDate, toDate);
    });
  },

  // BOOKING MANAGEMENT AUTHENTICATED METHODS
  async getBusinessBookings(fromDate, toDate, status) {
    return makeAuthenticatedRequest(async (token) => {
      return apiService.getBusinessBookings(token, fromDate, toDate, status);
    });
  },

  async getEmployeeCalendar(employeeId, fromDate, toDate) {
    return makeAuthenticatedRequest(async (token) => {
      return apiService.getEmployeeCalendar(token, employeeId, fromDate, toDate);
    });
  },

  async createBooking(bookingData) {
    return makeAuthenticatedRequest(async (token) => {
      return apiService.createBooking(token, bookingData);
    });
  },

  async updateBooking(bookingId, updates) {
    return makeAuthenticatedRequest(async (token) => {
      return apiService.updateBooking(token, bookingId, updates);
    });
  },

  async updateEmployeeBookingNotes(bookingId, notes) {
    return makeAuthenticatedRequest(async (token) => {
      return apiService.updateEmployeeBookingNotes(token, bookingId, notes);
    });
  },

  async updateBookingStatus(bookingId, newStatus) {
    return makeAuthenticatedRequest(async (token) => {
      return apiService.updateBookingStatus(token, bookingId, newStatus);
    });
  },

  async deleteBooking(bookingId) {
    return makeAuthenticatedRequest(async (token) => {
      return apiService.deleteBooking(token, bookingId);
    });
  },

  async getBookingDetails(bookingId) {
    return makeAuthenticatedRequest(async (token) => {
      return apiService.getBookingDetails(token, bookingId);
    });
  },

  async addServiceToBooking(bookingId, serviceData) {
    return makeAuthenticatedRequest(async (token) => {
      return apiService.addServiceToBooking(token, bookingId, serviceData);
    });
  },

  async removeServiceFromBooking(bookingServiceId) {
    return makeAuthenticatedRequest(async (token) => {
      return apiService.removeServiceFromBooking(token, bookingServiceId);
    });
  },

  async updateBookingServices(bookingId, services) {
    return makeAuthenticatedRequest(async (token) => {
      return apiService.updateBookingServices(token, bookingId, services);
    });
  },

  async getAvailableEmployees(serviceId, bookingDate, duration) {
    return makeAuthenticatedRequest(async (token) => {
      return apiService.getAvailableEmployees(token, serviceId, bookingDate, duration);
    });
  },

  async getEmployeeBookingsInRange(userId, startDate, endDate) {
    return makeAuthenticatedRequest(async (token) => {
      return apiService.getEmployeeBookingsInRange(token, userId, startDate, endDate);
    });
  },

  async getClientBookingHistory(clientId) {
    return makeAuthenticatedRequest(async (token) => {
      return apiService.getClientBookingHistory(token, clientId);
    });
  },

  async validateBookingConflicts(bookingDate, duration, userIds, bookingId = null, workstationIds = null) {
    return makeAuthenticatedRequest(async (token) => {
      return apiService.validateBookingConflicts(token, bookingDate, duration, userIds, bookingId, workstationIds);
    });
  },

  async getWorkstationAvailability(bookingDate, duration, bookingId = null) {
    return makeAuthenticatedRequest(async (token) => {
      return apiService.getWorkstationAvailability(token, bookingDate, duration, bookingId);
    });
  },

  // Workstations methods
  async getWorkstations() {
    return makeAuthenticatedRequest(async (token) => {
      return apiService.getWorkstations(token);
    });
  },

  async addWorkstation(workstationName, workstationDescription, capacity = 1) {
    return makeAuthenticatedRequest(async (token) => {
      return apiService.addWorkstation(token, workstationName, workstationDescription, capacity);
    });
  },

  async updateWorkstation(workstationId, updates) {
    return makeAuthenticatedRequest(async (token) => {
      return apiService.updateWorkstation(token, workstationId, updates);
    });
  },

  async deleteWorkstation(workstationId, permanent = false) {
    return makeAuthenticatedRequest(async (token) => {
      return apiService.deleteWorkstation(token, workstationId, permanent);
    });
  },

  async getBusinessClients() {
    return makeAuthenticatedRequest(async (token) => {
      return apiService.getBusinessClients(token);
    });
  },

  // Client notes methods
  async getClientNotes(clientId) {
    return makeAuthenticatedRequest(async (token) => {
      return apiService.getClientNotes(token, clientId);
    });
  },

  async addClientNote(clientId, noteContent) {
    return makeAuthenticatedRequest(async (token) => {
      return apiService.addClientNote(token, clientId, noteContent);
    });
  },

  async deleteClientNote(noteId) {
    return makeAuthenticatedRequest(async (token) => {
      return apiService.deleteClientNote(token, noteId);
    });
  },

  async getBusinessServices() {
    return makeAuthenticatedRequest(async (token) => {
      return apiService.getBusinessServices(token);
    });
  },

  async getEmployees() {
    return makeAuthenticatedRequest(async (token) => {
      return apiService.getEmployees(token);
    });
  },

  async updateEmployeeTargets(employeeId, targets, workSchedule = null, commissionSettings = null) {
    return makeAuthenticatedRequest(async (token) => {
      return apiService.updateEmployeeTargets(token, employeeId, targets, workSchedule, commissionSettings);
    });
  },

  async updateEmployeeTargetsSelf(targets, workSchedule = null) {
    return makeAuthenticatedRequest(async (token) => {
      return apiService.updateEmployeeTargetsSelf(token, targets, workSchedule);
    });
  },

  async editEmployeeProfileBusiness(employeeId, compensationData) {
    return makeAuthenticatedRequest(async (token) => {
      return apiService.editEmployeeProfileBusiness(token, employeeId, compensationData);
    });
  },

  async getEmployeeProfile(employeeId) {
    return makeAuthenticatedRequest(async (token) => {
      return apiService.getEmployeeProfile(token, employeeId);
    });
  },

  async editEmployeeProfileSelf(profileData) {
    return makeAuthenticatedRequest(async (token) => {
      return apiService.editEmployeeProfileSelf(token, profileData);
    });
  },

  // Missing ManageBookings functions
  async getAllEmployeesCalendar(fromDate, toDate) {
    return makeAuthenticatedRequest(async (token) => {
      return apiService.getAllEmployeesCalendar(token, fromDate, toDate);
    });
  },

  async processBookingPayment(bookingId, paymentAmount, paymentMethod) {
    return makeAuthenticatedRequest(async (token) => {
      return apiService.processBookingPayment(token, bookingId, paymentAmount, paymentMethod);
    });
  },

  async getBookingAnalytics(fromDate, toDate, groupBy) {
    return makeAuthenticatedRequest(async (token) => {
      return apiService.getBookingAnalytics(token, fromDate, toDate, groupBy);
    });
  },

  async autoCalculateMetrics(bookingId) {
    return makeAuthenticatedRequest(async (token) => {
      return apiService.autoCalculateMetrics(token, bookingId);
    });
  },

  async getBookingsByStatus(status, fromDate, toDate) {
    return makeAuthenticatedRequest(async (token) => {
      return apiService.getBookingsByStatus(token, status, fromDate, toDate);
    });
  },

  async bulkUpdateBookingStatus(bookingIds, newStatus) {
    return makeAuthenticatedRequest(async (token) => {
      return apiService.bulkUpdateBookingStatus(token, bookingIds, newStatus);
    });
  },

  // ManagePictures wrapper functions
  async uploadProfilePicture(imageData, targetUserId = null) {
    return makeAuthenticatedRequest(async (token) => {
      return apiService.uploadProfilePicture(token, imageData, targetUserId);
    });
  },

  async getProfilePicture(userId = null) {
    return makeAuthenticatedRequest(async (token) => {
      return apiService.getProfilePicture(token, userId);
    });
  },

  async deleteProfilePicture(userId = null) {
    return makeAuthenticatedRequest(async (token) => {
      return apiService.deleteProfilePicture(token, userId);
    });
  },

  // Business Logo wrapper functions
  async uploadBusinessLogo(imageData) {
    return makeAuthenticatedRequest(async (token) => {
      return apiService.uploadBusinessLogo(token, imageData);
    });
  },

  async getBusinessLogo(businessId = null) {
    return makeAuthenticatedRequest(async (token) => {
      return apiService.getBusinessLogo(token, businessId);
    });
  },

  async deleteBusinessLogo() {
    return makeAuthenticatedRequest(async (token) => {
      return apiService.deleteBusinessLogo(token);
    });
  },

  // ==================== MANAGE SERVICES WRAPPER ====================

  async getServices(isActive = null) {
    return makeAuthenticatedRequest(async (token) => {
      return apiService.getServices(token, isActive);
    });
  },

  async getService(serviceId) {
    return makeAuthenticatedRequest(async (token) => {
      return apiService.getService(token, serviceId);
    });
  },

  async createService(serviceName, description = null, duration, price) {
    return makeAuthenticatedRequest(async (token) => {
      return apiService.createService(token, serviceName, description, duration, price);
    });
  },

  async updateService(serviceId, serviceName = null, description = null, duration = null, price = null) {
    return makeAuthenticatedRequest(async (token) => {
      return apiService.updateService(token, serviceId, serviceName, description, duration, price);
    });
  },

  async deactivateService(serviceId) {
    return makeAuthenticatedRequest(async (token) => {
      return apiService.deactivateService(token, serviceId);
    });
  },

  async reactivateService(serviceId) {
    return makeAuthenticatedRequest(async (token) => {
      return apiService.reactivateService(token, serviceId);
    });
  },

  async deleteService(serviceId) {
    return makeAuthenticatedRequest(async (token) => {
      return apiService.deleteService(token, serviceId);
    });
  },

  async exportServices(format = 'csv', includeAnalytics = false) {
    return makeAuthenticatedRequest(async (token) => {
      return apiService.exportServices(token, format, includeAnalytics);
    });
  },

  async getServiceAnalytics(serviceId = null, fromDate = null, toDate = null) {
    return makeAuthenticatedRequest(async (token) => {
      return apiService.getServiceAnalytics(token, serviceId, fromDate, toDate);
    });
  },

  async getPopularServices(fromDate = null, toDate = null, limit = 10) {
    return makeAuthenticatedRequest(async (token) => {
      return apiService.getPopularServices(token, fromDate, toDate, limit);
    });
  },

  async getServiceRevenue(fromDate = null, toDate = null, groupBy = 'day') {
    return makeAuthenticatedRequest(async (token) => {
      return apiService.getServiceRevenue(token, fromDate, toDate, groupBy);
    });
  },

  // ============================================
  // LEAVE MANAGEMENT METHODS WITH AUTO REFRESH
  // ============================================

  async viewLeaveRequests(status = null, employeeId = null, fromDate = null, toDate = null) {
    return makeAuthenticatedRequest(async (token) => {
      return apiService.viewLeaveRequests(token, status, employeeId, fromDate, toDate);
    });
  },

  async reviewLeaveRequest(leaveRequestId, status, reviewComments = null, allowNegativeBalance = false, overrideReason = null) {
    return makeAuthenticatedRequest(async (token) => {
      return apiService.reviewLeaveRequest(token, leaveRequestId, status, reviewComments, allowNegativeBalance, overrideReason);
    });
  },

  async getLeaveBalances(userId = null) {
    return makeAuthenticatedRequest(async (token) => {
      return apiService.getLeaveBalances(token, userId);
    });
  },

  async getLeavePolicyGroups() {
    return makeAuthenticatedRequest(async (token) => {
      return apiService.getLeavePolicyGroups(token);
    });
  },

  async createLeavePolicyGroup(groupName, groupDescription = null) {
    return makeAuthenticatedRequest(async (token) => {
      return apiService.createLeavePolicyGroup(token, groupName, groupDescription);
    });
  },

  async editLeavePolicyGroup(policyGroupId, groupName, groupDescription = null) {
    return makeAuthenticatedRequest(async (token) => {
      return apiService.editLeavePolicyGroup(token, policyGroupId, groupName, groupDescription);
    });
  },

  async deleteLeavePolicyGroup(policyGroupId) {
    return makeAuthenticatedRequest(async (token) => {
      return apiService.deleteLeavePolicyGroup(token, policyGroupId);
    });
  },

  async getLeavePolicies(policyGroupId = null) {
    return makeAuthenticatedRequest(async (token) => {
      return apiService.getLeavePolicies(token, policyGroupId);
    });
  },

  async createLeavePolicy(policyGroupId, leaveTypeId, entitlement, accrualPeriod, maxBalance, isPaid) {
    return makeAuthenticatedRequest(async (token) => {
      return apiService.createLeavePolicy(token, policyGroupId, leaveTypeId, entitlement, accrualPeriod, maxBalance, isPaid);
    });
  },

  async editLeavePolicy(leavePolicyId, entitlement, accrualPeriod, maxBalance, isPaid) {
    return makeAuthenticatedRequest(async (token) => {
      return apiService.editLeavePolicy(token, leavePolicyId, entitlement, accrualPeriod, maxBalance, isPaid);
    });
  },

  async deleteLeavePolicy(leavePolicyId) {
    return makeAuthenticatedRequest(async (token) => {
      return apiService.deleteLeavePolicy(token, leavePolicyId);
    });
  },

  async getLeaveTypes() {
    return makeAuthenticatedRequest(async (token) => {
      return apiService.getLeaveTypes(token);
    });
  },

  async getMinimumRequirements() {
    return makeAuthenticatedRequest(async (token) => {
      return apiService.getMinimumRequirements(token);
    });
  },

  async assignEmployeeToPolicyGroup(userId, policyGroupId) {
    return makeAuthenticatedRequest(async (token) => {
      return apiService.assignEmployeeToPolicyGroup(token, userId, policyGroupId);
    });
  },

  async createLeaveRequest(leaveTypeId, startDate, endDate, notes = null) {
    return makeAuthenticatedRequest(async (token) => {
      return apiService.createLeaveRequest(token, leaveTypeId, startDate, endDate, notes);
    });
  },

  async getLeaveCalendar(fromDate = null, toDate = null) {
    return makeAuthenticatedRequest(async (token) => {
      return apiService.getLeaveCalendar(token, fromDate, toDate);
    });
  },

  async getLeaveAnalytics(fromDate = null, toDate = null) {
    return makeAuthenticatedRequest(async (token) => {
      return apiService.getLeaveAnalytics(token, fromDate, toDate);
    });
  },

  // Checklist methods with automatic token refresh
  async getChecklistInstances(userId = null, status = null, fromDate = null, toDate = null) {
    return makeAuthenticatedRequest(async (token) => {
      return apiService.getChecklistInstances(token, userId, status, fromDate, toDate);
    });
  },

  async getChecklist(checklistId) {
    return makeAuthenticatedRequest(async (token) => {
      return apiService.getChecklist(token, checklistId);
    });
  },

  async startChecklistInstance(instanceId) {
    return makeAuthenticatedRequest(async (token) => {
      return apiService.startChecklistInstance(token, instanceId);
    });
  },

  async submitChecklistInstance(instanceId, submissionData) {
    return makeAuthenticatedRequest(async (token) => {
      return apiService.submitChecklistInstance(token, instanceId, submissionData);
    });
  },

  async updateChecklistProgress(instanceId, progressData) {
    return makeAuthenticatedRequest(async (token) => {
      return apiService.updateChecklistProgress(token, instanceId, progressData);
    });
  },

  async getChecklistPerformance(userId = null, fromDate = null, toDate = null) {
    return makeAuthenticatedRequest(async (token) => {
      return apiService.getChecklistPerformance(token, userId, fromDate, toDate);
    });
  },

  async getChecklists() {
    return makeAuthenticatedRequest(async (token) => {
      return apiService.getChecklists(token);
    });
  },

  async createChecklist(title, description, content) {
    return makeAuthenticatedRequest(async (token) => {
      return apiService.createChecklist(token, title, description, content);
    });
  },

  async editChecklist(checklistId, title, description, content) {
    return makeAuthenticatedRequest(async (token) => {
      return apiService.editChecklist(token, checklistId, title, description, content);
    });
  },

  async deleteChecklist(checklistId) {
    return makeAuthenticatedRequest(async (token) => {
      return apiService.deleteChecklist(token, checklistId);
    });
  },

  async createChecklistSchedule(checklistId, scheduleName, frequency, dayOfWeek, dayOfMonth, assignedUserIds) {
    return makeAuthenticatedRequest(async (token) => {
      return apiService.createChecklistSchedule(token, checklistId, scheduleName, frequency, dayOfWeek, dayOfMonth, assignedUserIds);
    });
  },

  async getChecklistSchedules(checklistId = null) {
    return makeAuthenticatedRequest(async (token) => {
      return apiService.getChecklistSchedules(token, checklistId);
    });
  },

  async editChecklistSchedule(scheduleId, scheduleName, frequency, dayOfWeek, dayOfMonth) {
    return makeAuthenticatedRequest(async (token) => {
      return apiService.editChecklistSchedule(token, scheduleId, scheduleName, frequency, dayOfWeek, dayOfMonth);
    });
  },

  async assignUsersToSchedule(scheduleId, assignedUserIds) {
    return makeAuthenticatedRequest(async (token) => {
      return apiService.assignUsersToSchedule(token, scheduleId, assignedUserIds);
    });
  },

  async deleteChecklistSchedule(scheduleId) {
    return makeAuthenticatedRequest(async (token) => {
      return apiService.deleteChecklistSchedule(token, scheduleId);
    });
  },

  async createManualInstance(checklistId, userId, instanceDate, dueTime) {
    return makeAuthenticatedRequest(async (token) => {
      return apiService.createManualInstance(token, checklistId, userId, instanceDate, dueTime);
    });
  },

  async updateChecklistInstance(instanceId, instanceDate, dueTime, userId) {
    return makeAuthenticatedRequest(async (token) => {
      return apiService.updateChecklistInstance(token, instanceId, instanceDate, dueTime, userId);
    });
  },

  async deleteChecklistInstance(instanceId) {
    return makeAuthenticatedRequest(async (token) => {
      return apiService.deleteChecklistInstance(token, instanceId);
    });
  },

  async triggerScheduleGeneration() {
    return makeAuthenticatedRequest(async (token) => {
      return apiService.triggerScheduleGeneration(token);
    });
  },

  // Notification methods with automatic token refresh
  async getNotifications(unreadOnly = false) {
    return makeAuthenticatedRequest(async (token) => {
      return apiService.getNotifications(token, unreadOnly);
    });
  },

  async markNotificationAsRead(notificationId) {
    return makeAuthenticatedRequest(async (token) => {
      return apiService.markNotificationAsRead(token, notificationId);
    });
  },

  async dismissNotification(notificationId) {
    return makeAuthenticatedRequest(async (token) => {
      return apiService.dismissNotification(token, notificationId);
    });
  },

  async getPendingReviews() {
    return makeAuthenticatedRequest(async (token) => {
      return apiService.getPendingReviews(token);
    });
  },

  async reviewChecklistSubmission(submissionId, taskScores, totalScore, comments) {
    return makeAuthenticatedRequest(async (token) => {
      return apiService.reviewChecklistSubmission(token, submissionId, taskScores, totalScore, comments);
    });
  },

  // Stock Management Methods
  async getStockItems(filters = {}) {
    return makeAuthenticatedRequest(async (token) => {
      return apiService.getStockItems(token, filters);
    });
  },

  async getStockItem(stockItemId) {
    return makeAuthenticatedRequest(async (token) => {
      return apiService.getStockItem(token, stockItemId);
    });
  },

  async createStockItem(itemData) {
    return makeAuthenticatedRequest(async (token) => {
      return apiService.createStockItem(token, itemData);
    });
  },

  async updateStockItem(stockItemId, itemData) {
    return makeAuthenticatedRequest(async (token) => {
      return apiService.updateStockItem(token, stockItemId, itemData);
    });
  },

  async deactivateStockItem(stockItemId) {
    return makeAuthenticatedRequest(async (token) => {
      return apiService.deactivateStockItem(token, stockItemId);
    });
  },

  async purchaseStock(stockItemId, quantity, unitPrice, supplier, notes) {
    return makeAuthenticatedRequest(async (token) => {
      return apiService.purchaseStock(token, stockItemId, quantity, unitPrice, supplier, notes);
    });
  },

  async sellStock(stockItemId, quantity, unitPrice, notes, customerName = null, clientId = null, bookingId = null, reference = null) {
    return makeAuthenticatedRequest(async (token) => {
      return apiService.sellStock(token, stockItemId, quantity, unitPrice, notes, customerName, clientId, bookingId, reference);
    });
  },

  // ===== VOUCHER MANAGEMENT =====

  async getVoucherTypes(filters = {}) {
    return makeAuthenticatedRequest(async (token) => {
      return apiService.getVoucherTypes(token, filters);
    });
  },

  async createVoucherType(voucherTypeData) {
    return makeAuthenticatedRequest(async (token) => {
      return apiService.createVoucherType(token, voucherTypeData);
    });
  },

  async updateVoucherType(voucherTypeId, updates) {
    return makeAuthenticatedRequest(async (token) => {
      return apiService.updateVoucherType(token, voucherTypeId, updates);
    });
  },

  async deactivateVoucherType(voucherTypeId) {
    return makeAuthenticatedRequest(async (token) => {
      return apiService.deactivateVoucherType(token, voucherTypeId);
    });
  },

  async purchaseVoucher(voucherTypeId, clientId = null, quantity = 1, customAmount) {
    return makeAuthenticatedRequest(async (token) => {
      return apiService.purchaseVoucher(token, voucherTypeId, clientId, quantity, customAmount);
    });
  },

  async validateVoucher(voucherCode, cartTotal = 0) {
    return makeAuthenticatedRequest(async (token) => {
      return apiService.validateVoucher(token, voucherCode, cartTotal);
    });
  },

  async redeemVoucher(voucherCode, saleReference = null) {
    return makeAuthenticatedRequest(async (token) => {
      return apiService.redeemVoucher(token, voucherCode, saleReference);
    });
  },

  async getVoucherInstances(filters = {}) {
    return makeAuthenticatedRequest(async (token) => {
      return apiService.getVoucherInstances(token, filters);
    });
  },

  async adjustStock(stockItemId, adjustment, reason, adjustmentReasonType, notes) {
    return makeAuthenticatedRequest(async (token) => {
      return apiService.adjustStock(token, stockItemId, adjustment, reason, adjustmentReasonType, notes);
    });
  },

  // Alias for adjustStock (used by StockManagementPage)
  async adjustStockQuantity(stockItemId, adjustment, reason, adjustmentReasonType, notes) {
    return this.adjustStock(stockItemId, adjustment, reason, adjustmentReasonType, notes);
  },

  async getStockTransactions(stockItemId, filters = {}) {
    return makeAuthenticatedRequest(async (token) => {
      return apiService.getStockTransactions(token, stockItemId, filters);
    });
  },

  async getStockLevels(filters = {}) {
    return makeAuthenticatedRequest(async (token) => {
      return apiService.getStockLevels(token, filters);
    });
  },

  async getLowStockAlerts() {
    return makeAuthenticatedRequest(async (token) => {
      return apiService.getLowStockAlerts(token);
    });
  },

  async getStockAnalytics(startDate, endDate) {
    return makeAuthenticatedRequest(async (token) => {
      return apiService.getStockAnalytics(token, startDate, endDate);
    });
  },

  async exportStockData(format = 'csv') {
    return makeAuthenticatedRequest(async (token) => {
      return apiService.exportStockData(token, format);
    });
  },

  async getUnlinkedRetailSales(fromDate, toDate) {
    return makeAuthenticatedRequest(async (token) => {
      return apiService.getUnlinkedRetailSales(token, fromDate, toDate);
    });
  },

  async searchClients(searchTerm, searchType = 'name') {
    return makeAuthenticatedRequest(async (token) => {
      return apiService.searchClients(token, searchTerm, searchType);
    });
  },

  async getNewClients(fromDate, toDate) {
    return makeAuthenticatedRequest(async (token) => {
      return apiService.getNewClients(token, fromDate, toDate);
    });
  },

  async getClientRetention() {
    return makeAuthenticatedRequest(async (token) => {
      return apiService.getClientRetention(token);
    });
  },

  async getBusinessProfile() {
    return makeAuthenticatedRequest(async (token) => {
      return apiService.getBusinessProfile(token);
    });
  },

  async editBusinessProfile(businessData) {
    return makeAuthenticatedRequest(async (token) => {
      return apiService.editBusinessProfile(token, businessData);
    });
  },

  async getBusinessSettings() {
    return makeAuthenticatedRequest(async (token) => {
      return apiService.getBusinessSettings(token);
    });
  },

  // ===== Billing / Subscription methods with auto-refresh =====

  async getActiveSubscription() {
    return makeAuthenticatedRequest(async (token) => {
      return apiService.getActiveSubscription(token);
    });
  },

  async getPaymentHistory() {
    return makeAuthenticatedRequest(async (token) => {
      return apiService.getPaymentHistory(token);
    });
  },

  async getPaymentMethods() {
    return makeAuthenticatedRequest(async (token) => {
      return apiService.getPaymentMethods(token);
    });
  },

  async initializeSubscriptionPayment(planData) {
    return makeAuthenticatedRequest(async (token) => {
      return apiService.initializeSubscriptionPayment(token, planData);
    });
  },

  async verifySubscriptionPayment(reference) {
    return makeAuthenticatedRequest(async (token) => {
      return apiService.verifySubscriptionPayment(token, reference);
    });
  },

  async getInvoices() {
    return makeAuthenticatedRequest(async (token) => {
      return apiService.getInvoices(token);
    });
  },

  async getBookings(filters = {}) {
    return makeAuthenticatedRequest(async (token) => {
      // Use getBusinessBookings with flexible filters
      // Convert filters to the format expected by getBusinessBookings
      const { searchTerm, status, limit, fromDate, toDate, clientId, pageSize } = filters;

      // Call getBusinessBookings with the provided filters
      const response = await apiService.getBusinessBookings(
        token,
        fromDate || null,
        toDate || null,
        status || null,
        clientId || null,
        pageSize || null
      );

      // If searchTerm provided, filter results on client-side
      if (response.success && response.data && searchTerm) {
        const filtered = response.data.filter(booking => {
          const searchLower = searchTerm.toLowerCase();
          const clientName = `${booking.clientFirstName || ''} ${booking.clientLastName || ''}`.toLowerCase();
          const bookingNumber = (booking.bookingNumber || '').toString().toLowerCase();
          const bookingId = (booking.id || '').toString().toLowerCase();

          return clientName.includes(searchLower) ||
                 bookingNumber.includes(searchLower) ||
                 bookingId.includes(searchLower);
        });

        response.data = limit ? filtered.slice(0, limit) : filtered;
      } else if (response.success && response.data && limit) {
        response.data = response.data.slice(0, limit);
      }

      return response;
    });
  },

  // ===== Support System =====

  async createChatSession(data) {
    return makeAuthenticatedRequest(async (token) => apiService.createChatSession(token, data));
  },

  async getConversations(status) {
    return makeAuthenticatedRequest(async (token) => apiService.getConversations(token, status));
  },

  async getMessages(conversationId, page, pageSize) {
    return makeAuthenticatedRequest(async (token) => apiService.getMessages(token, conversationId, page, pageSize));
  },

  async sendMessage(conversationId, message) {
    return makeAuthenticatedRequest(async (token) => apiService.sendMessage(token, conversationId, message));
  },

  async pollMessages(conversationId, since) {
    return makeAuthenticatedRequest(async (token) => apiService.pollMessages(token, conversationId, since));
  },

  async submitBugReport(data) {
    return makeAuthenticatedRequest(async (token) => apiService.submitBugReport(token, data));
  },

  async getNotificationPreferences() {
    return makeAuthenticatedRequest(async (token) => apiService.getNotificationPreferences(token));
  },

  async updateNotificationPreferences(data) {
    return makeAuthenticatedRequest(async (token) => apiService.updateNotificationPreferences(token, data));
  },

  async getUnreadCount() {
    return makeAuthenticatedRequest(async (token) => apiService.getUnreadCount(token));
  }
};

// Token utility functions (referenced by makeAuthenticatedRequest and exported for external use)
export const tokenUtils = {
  // Store tokens in localStorage or sessionStorage based on keepSignedIn
  storeTokens(token, refreshKey, keepSignedIn = true) {
    const storage = keepSignedIn ? localStorage : sessionStorage;
    storage.setItem('sessionToken', token);
    storage.setItem('refreshKey', refreshKey);

    // Clear from the other storage to avoid conflicts
    const otherStorage = keepSignedIn ? sessionStorage : localStorage;
    otherStorage.removeItem('sessionToken');
    otherStorage.removeItem('refreshKey');
  },

  // Get stored tokens from both localStorage and sessionStorage
  getTokens() {
    // Check localStorage first, then sessionStorage
    let token = localStorage.getItem('sessionToken');
    let refreshKey = localStorage.getItem('refreshKey');

    if (!token) {
      token = sessionStorage.getItem('sessionToken');
      refreshKey = sessionStorage.getItem('refreshKey');
    }

    return {
      token,
      refreshKey
    };
  },

  // Clear stored tokens from both storages
  clearTokens() {
    localStorage.removeItem('sessionToken');
    localStorage.removeItem('refreshKey');
    sessionStorage.removeItem('sessionToken');
    sessionStorage.removeItem('refreshKey');
  },

  // Decode token to get user info (simple base64 decode - not for security validation!)
  decodeToken(token) {
    try {
      if (!token) return { success: false, error: 'No token provided' };
      const payload = token.split('.')[1];
      const decoded = JSON.parse(atob(payload));
      return {
        success: true,
        data: {
          userId: decoded.userId || decoded.sub,
          employeeId: decoded.employeeId,
          role: decoded.role,
          businessId: decoded.businessId || decoded.tennantId || decoded.tenantId,
          tokenType: decoded.tokenType,
          accessBusinessAccount: decoded.accessBusinessAccount,
          email: decoded.email,
          iat: decoded.iat,
          exp: decoded.exp
        }
      };
    } catch (error) {
      return {
        success: false,
        error: 'Invalid token format'
      };
    }
  }
};
